import {
  Company,
  CompanyInfo,
  CompanyRole,
  CompanyroleId,
  SuggestedCompany,
} from "../../types/CompanyInfo";
import { ShareholderData, SharesForClass } from "../../types/Shareholder";
import { plusYears, toYearISOString } from "../../utils/Dates";
import { compact, filter, get, isEqual, last, maxBy, sortBy, unionBy, without } from "lodash";
import { getFullName } from "../../utils/Roles";
import { User } from "../../types/UserInfo";
import { formatPhoneNumberToE164 } from "../../utils/phoneNumbers";
import {
  ArticleChangeType,
  BR_STATUS,
  CompanyItem,
  CustomizedFreetextType,
  DocumentLanguage,
  failure,
  Failure,
  FreetextType,
  GetProjectDocumentSignersResponse,
  isOk,
  LocalizedText,
  LocalizedTextForDocument,
  ok,
  Ok,
  pending,
  PhoneNumber,
  ProjectDocumentPreview,
  Result,
  SelectableWithId,
  Signer,
  SigningMethod,
  SubscriptionTypeModel,
  TranslationOption,
} from "../../common/common";
import { Decision, DecisionData, Document, DocumentOverrides } from "../project/projectDomain";
import { isProductionEnvironment } from "../../utils/environments";
import {
  DocumentSigningStatus,
  GetDocumentSigningStatusResponse,
  GetSubscriptionStatusResponse,
} from "../../document/documentApi";
import {
  FileUploadConfig,
  ProjectDecisionAttachment,
  UploadedAttachment,
} from "../../fileUpload/fileUploadDomain";
import { EmailStatus } from "../../types/SavedDocument";
import { BoardMeeting, BoardMeetingFields, ProjectCompanyRole } from "./commonBoardMeetingDomain";
import { CreateAndSendMessageRequest } from "../../messaging/adminMessagingDomain";
import { AIClient } from "../../ai/AIAssistant";
import {
  ContributionPerShareType,
  IncreaseOfShareCapitalType,
  PeriodForSubscription,
  ShareIssuePerShareType,
  SubscriberForIncreaseNominalValue,
  SubscriberForIssue,
  SubscriberSigner,
} from "../increaseOfShareCapital/increaseOfShareCapitalDomain";
import { IconName } from "../../components-v2/iconMapping";
import { FreetextTypeForDocument } from "../freetext/freetextDomain";
import { IconProps } from "../../components-v2/Icon";
import { OptionsBeforeSending } from "../summary/summaryDomain";
import { BoardMeetingVote } from "../boardMeetingMinutes/boardMeetingVoting";
import { UserPrefs } from "../../stores/userPrefsDomain";
import {
  ElectronicGeneralMeeting,
  GeneralMeetingStatus,
  StatusName,
} from "../../meetings/generalMeeting/electronicGeneralMeetingDomain";
import { Language } from "../../i18n/languages";
import {
  GeneralMeeting,
  GeneralMeetingFields,
  GeneralMeetingVotes,
  GeneralMeetingVoting,
  GeneralMeetingVotingType,
} from "./commonGeneralMeetingDomain";
import {
  GeneralMeetingVoteType,
  PoARecipientRole,
} from "../../meetings/generalMeeting/generalMeetingDomain";
import { when } from "../../utils/functions";
import { formatOrgNum } from "../../utils/other";
import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import { DocumentType, getDocumentLabel } from "../../common/documents";
import { ArticlesOfAssociation } from "../../types/ArticlesOfAssociation";
import { FilePreviewProps } from "../../components-v2/FilePreview";
import { FileToPreview } from "../../components-v2/BatchPreviewDialog/batchPreviewDomain";
import { ChangeEvent } from "react";
import { defaultLanguageForOrgNo, isSwedishOrgNo } from "../../utils/orgNo";
import { getDecisionsByCategory } from "../projectList/projectListDomain";
import { AlertMessage } from "../../stores/AlertMessageStore";
import { DCEvent, EventDispatcher } from "../../events/eventsDomain";
import {
  LocalizedStringFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  SigningSettings,
} from "../../common/commonForms";
import { PaidSubscription, SubscriptionType } from "../../common/subscription";
import { RootStoreData } from "../../events/rootStoreData";
import { ShareType, ShareTypeCode } from "../../common/share";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";
import { FeatureToggles } from "../../common/featureToggles";
import { TMessageKey } from "../../i18n/types.gen";
import {
  CustomDocumentType,
  customDocumentTypeLabels,
  customDocumentTypes,
} from "../customDocument/customDocumentDomain";
import { DataRequest, DataResponse } from "../../ai/autotext";
import { EventLog, EventType } from "../../common/eventLogs";
import { getAllCountriesSortedByName, norway } from "../../utils/countries";

///
/// Project
///

export enum ProjectStatus {
  DRAFT = "DRAFT",
  DOCUMENT_SIGNING_STARTED = "DOCUMENT_SIGNING_STARTED", //legacy, to be removed
  SENT = "SENT",
  FOLLOW_UP = "FOLLOW_UP",
  COMPLETED = "COMPLETED",
}

export const projectStatusLabels: Record<ProjectStatus, TMessageKey> = {
  [ProjectStatus.DRAFT]: "projectStatus.DRAFT",
  [ProjectStatus.DOCUMENT_SIGNING_STARTED]: "projectStatus.SENT",
  [ProjectStatus.SENT]: "projectStatus.SENT",
  [ProjectStatus.FOLLOW_UP]: "projectStatus.FOLLOW_UP",
  [ProjectStatus.COMPLETED]: "projectStatus.COMPLETED",
};

export interface ProjectJsonData {
  decisions: Decision[];
  documents: Document[];
  boardMeeting?: BoardMeeting;
  generalMeeting?: GeneralMeeting;
  roles: ProjectCompanyRole[];
  inputStates?: InputState[];
  initialState?: Evaluation;
  data: DataRequest[];
}

export function createProjectJsonData(jsonData?: Partial<ProjectJsonData>): ProjectJsonData {
  return {
    ...jsonData,
    decisions: jsonData?.decisions ?? [],
    documents: jsonData?.documents ?? [],
    roles: jsonData?.roles ?? [],
    data: jsonData?.data ?? [],
  };
}

export interface ProjectsForCopyResponse {
  projects: ProjectForCopy[];
}

export interface ProjectForCopy {
  projectId: string;
  title: string;
  decisions: DecisionForCopy[];
}

export interface DecisionForCopy {
  decisionId: string;
  decisionType: DecisionType;
  freetextTitles: LocalizedText[];
}

export interface GetProjectListResponse {
  projects: ProjectListItem[];
}

export interface ProjectListItem {
  id: string;
  organizationnumber: string;
  projectName: string;
  projectStatus: ProjectStatus;
  updatedTime: string;
  hasBoardMeeting: boolean;
  hasGeneralMeeting: boolean;
  containsNoticeOrMinutesBoardMeetingDocument: boolean;
  containsNoticeOrMinutesGeneralMeetingDocument: boolean;
  boardMeetingDate?: string;
  boardMeetingTime?: string;
  boardMeetingNumber?: string;
  useBoardMeetingNumber?: boolean;
  generalMeetingDate?: string;
  generalMeetingTime?: string;
  isBatchProject: boolean;
  isOwner: boolean;
  batchProjectCompanyName?: string;
  numberOfPermissions: number;
}

export interface CreateProjectRequest {
  orgNo: string;
  projectName: string;
  isGeneratedName?: boolean;
  decisions?: DecisionType[];
  documents?: DocumentType[];
  duplicateProjectId?: string;
  customDocumentType?: CustomDocumentType;
}

export function mapToCreateProjectRequest(
  orgNo: string,
  projectName: string,
  duplicateProjectId?: string,
  decisions?: DecisionType[],
  documents?: DocumentType[],
): CreateProjectRequest {
  return {
    orgNo: orgNo,
    projectName: projectName,
    decisions: decisions,
    documents: documents,
    duplicateProjectId: duplicateProjectId,
  };
}

export type CreateProjectResponse = { projectId: string };

export type DocumentPreview = {
  documentId: string;
  content: string;
  batchPreviews: {
    [orgNo: string]: DocumentPreview;
  };
};

export type ProjectBatchDecisionData = {
  targetDecisionId: string;
};

export type ProjectBatchDocumentData = {
  targetDocumentId: string;
  overrides?: DocumentOverrides;
};

export type ProjectBatchData = {
  decisionMapping: {
    [decisionId: string]: ProjectBatchDecisionData;
  };
  documentMapping: {
    [documentId: string]: ProjectBatchDocumentData;
  };
};

export type ProjectCompany = { orgNo: string; name: string; isMainCompany?: boolean };

export type ProjectCompanyContext = {
  subscribers?: Subscriber[];
  companyInfo: CompanyInfo;
  shareholderData: ShareholderData;
};

export type BatchDocumentOverrrides = {
  documentId: string;
  orgNo: string;
  overrides?: DocumentOverrides;
};

export type GetProjectResponse = {
  projectId: string;
  version: number;
  name?: string;
  generatedName?: string;
  projectStatus: ProjectStatus;
  jsonData: ProjectJsonData;
  previews: DocumentPreview[];
  documentStatuses: ProjectDocumentStatus[];
  isLocked: boolean;
  isDeleted: boolean;
  isReadonly: boolean;
  isSharepointEnabled: boolean;
  followUps: Record<string, DecisionFollowUp>;
  boardMeetingVotes: BoardMeetingVote[];
  electronicGeneralMeeting?: ElectronicGeneralMeeting;
  participationList: GeneralMeetingParticipation[];
  powerOfAttorneys: PowerOfAttorneyForGeneralMeeting[];
  batchData: { [orgNo: string]: ProjectBatchData };
  batchCompanies: string[];
  batchDocumentOverrides: BatchDocumentOverrrides[];
  isBatchProject: boolean;
  decisionsRequiresGeneralMeeting: string[];
  decisionsRequiringArticlesOfAssociation: string[];
  defaultDecisionsGeneralMeetingVotingType: Record<string, GeneralMeetingVotingType>;
  decisionsApprovedByGeneralMeeting: Record<string, boolean>;
  numberOfPermissions: number;
  data: {
    [orgNo: string]: { request: DataRequest; response: DataResponse }[];
  };
};

export type SelectBatchCompaniesRequest = {
  companies: string[];
};

///
/// Decisions
///

const enabledDecisionTypes: DecisionType[] = [
  DecisionType.approveAnnualAccount,
  DecisionType.approveAuditorRemuneration,
  DecisionType.approveCompanyBoardRemuneration,
  DecisionType.approveNominationCommitteeRemuneration,
  DecisionType.auditor,
  DecisionType.boardAssignsSignature,
  DecisionType.capitalIncreaseThroughBonusIssue,
  DecisionType.changeArticlesOfAssociation,
  DecisionType.changeCompanyBoardInArticlesOfAssociation,
  DecisionType.changeSignatureInArticlesOfAssociation,
  DecisionType.companyName,
  DecisionType.convertibleLoanPowerOfAttorney,
  DecisionType.conversionOfShares,
  DecisionType.dividend,
  DecisionType.dividendPowerOfAttorney,
  DecisionType.electionOfCompanyBoard,
  DecisionType.electNominationCommittee,
  DecisionType.employmentOfManagingDirector,
  DecisionType.freetext,
  DecisionType.groupContributionByGeneralMeeting,
  DecisionType.increaseOfShareCapital,
  DecisionType.increaseShareCapitalPowerOfAttorney,
  DecisionType.insertCompanyBoardInArticlesOfAssociation,
  DecisionType.insertSignatureInArticlesOfAssociation,
  DecisionType.noAuditor,
  DecisionType.procura,
  DecisionType.reductionOfShareCapital,
  DecisionType.removeCompanyBoardFromArticlesOfAssociation,
  DecisionType.removeSignatureFromArticlesOfAssociation,
  DecisionType.reverseSplitShare,
  DecisionType.splitShare,
  DecisionType.acquireOwnSharesPowerOfAttorney,
];

export const enabledBatchDecisionTypes: DecisionType[] = [
  DecisionType.approveAnnualAccount,
  DecisionType.approveAuditorRemuneration,
  DecisionType.auditor,
  DecisionType.freetext,
];

export const decisionsRequiringCustomization = new Set([
  DecisionType.approveCompanyBoardRemuneration,
  DecisionType.assignsNewCompanyBoard,
  DecisionType.boardAssignsSignature,
  DecisionType.capitalIncreaseThroughBonusIssue,
  DecisionType.changeArticlesOfAssociation,
  DecisionType.changeCompanyBoardInArticlesOfAssociation,
  DecisionType.changeSignatureInArticlesOfAssociation,
  DecisionType.companyName,
  DecisionType.convertibleLoanPowerOfAttorney,
  DecisionType.dividend,
  DecisionType.employmentOfManagingDirector,
  DecisionType.reductionOfShareCapital,
  DecisionType.removeSignatureFromArticlesOfAssociation,
  DecisionType.reverseSplitShare,
  DecisionType.splitShare,
]);

const newDecisionTypes: DecisionType[] = [];

export function getEnabledDecisionTypes({
  isBatchProject,
}: {
  isBatchProject?: boolean;
}): DecisionType[] {
  if (isBatchProject) {
    return enabledBatchDecisionTypes;
  }
  return isProductionEnvironment() ? enabledDecisionTypes : getAllDecisionTypes();
}

export function getAllDecisionTypes(orgNo?: string): DecisionType[] {
  if (isSwedishOrgNo(orgNo)) {
    return [DecisionType.freetext, DecisionType.companyName, DecisionType.assignsNewCompanyBoard];
  }
  return Object.values(DecisionType);
}

export function isDecisionTypePreview(decisionType: DecisionType) {
  return !isProductionEnvironment() && !enabledDecisionTypes.includes(decisionType);
}

export function isDecisionTypeNew(decisionType: DecisionType) {
  return newDecisionTypes.includes(decisionType);
}

export enum DecisionCategory {
  ordinaryGeneralMeeting = "ordinaryGeneralMeeting",
  aboutTheCompany = "aboutTheCompany",
  companyRoles = "companyRoles",
  companyShareCapital = "companyShareCapital",
  changesToArticles = "changesToArticles",
  powerOfAttorneyFromGeneralMeeting = "powerOfAttorneyFromGeneralMeeting",
  other = "other",
}

export const decisionCategoryLabels: { [key in DecisionCategory]: TMessage } = {
  [DecisionCategory.ordinaryGeneralMeeting]: { key: "dbot.category.ordinaryGeneralMeeting" },
  [DecisionCategory.aboutTheCompany]: { key: "dbot.category.aboutTheCompany" },
  [DecisionCategory.companyRoles]: { key: "dbot.category.companyRoles" },
  [DecisionCategory.companyShareCapital]: { key: "dbot.category.companyShareCapital" },
  [DecisionCategory.changesToArticles]: { key: "dbot.category.changesToArticles" },
  [DecisionCategory.powerOfAttorneyFromGeneralMeeting]: {
    key: "dbot.category.powerOfAttorneyFromGeneralMeeting",
  },
  [DecisionCategory.other]: { key: "dbot.category.other" },
};

export const categorySubscriptionMapping: Record<DecisionCategory, SubscriptionType> = {
  [DecisionCategory.ordinaryGeneralMeeting]: SubscriptionType.BASIC,
  [DecisionCategory.aboutTheCompany]: SubscriptionType.PRO,
  [DecisionCategory.companyRoles]: SubscriptionType.PRO,
  [DecisionCategory.companyShareCapital]: SubscriptionType.PRO,
  [DecisionCategory.changesToArticles]: SubscriptionType.PRO,
  [DecisionCategory.powerOfAttorneyFromGeneralMeeting]: SubscriptionType.PRO,
  [DecisionCategory.other]: SubscriptionType.PRO,
};

export const categoryMapping: Record<DecisionType, DecisionCategory> = {
  [DecisionType.changeArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.companyName]: DecisionCategory.aboutTheCompany,
  [DecisionType.electionOfCompanyBoard]: DecisionCategory.companyRoles,
  [DecisionType.auditor]: DecisionCategory.companyRoles,
  [DecisionType.noAuditor]: DecisionCategory.companyRoles,
  [DecisionType.approveAnnualAccount]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.dividend]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.approveCompanyBoardRemuneration]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.electNominationCommittee]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.approveNominationCommitteeRemuneration]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.approveAuditorRemuneration]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.groupContributionByGeneralMeeting]: DecisionCategory.ordinaryGeneralMeeting,
  [DecisionType.boardAssignsSignature]: DecisionCategory.companyRoles,
  [DecisionType.changeSignatureInArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.employmentOfManagingDirector]: DecisionCategory.companyRoles,
  [DecisionType.increaseOfShareCapital]: DecisionCategory.companyShareCapital,
  [DecisionType.removeCompanyBoardFromArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.insertCompanyBoardInArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.removeSignatureFromArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.insertSignatureInArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.changeCompanyBoardInArticlesOfAssociation]: DecisionCategory.changesToArticles,
  [DecisionType.procura]: DecisionCategory.companyRoles,
  [DecisionType.assignsNewCompanyBoard]: DecisionCategory.companyRoles,
  [DecisionType.capitalIncreaseThroughBonusIssue]: DecisionCategory.companyShareCapital,
  [DecisionType.freetext]: DecisionCategory.other,
  [DecisionType.splitShare]: DecisionCategory.companyShareCapital,
  [DecisionType.reverseSplitShare]: DecisionCategory.companyShareCapital,
  [DecisionType.convertibleLoanPowerOfAttorney]: DecisionCategory.powerOfAttorneyFromGeneralMeeting,
  [DecisionType.dividendPowerOfAttorney]: DecisionCategory.powerOfAttorneyFromGeneralMeeting,
  [DecisionType.increaseShareCapitalPowerOfAttorney]:
    DecisionCategory.powerOfAttorneyFromGeneralMeeting,
  [DecisionType.reductionOfShareCapital]: DecisionCategory.companyShareCapital,
  [DecisionType.conversionOfShares]: DecisionCategory.companyShareCapital,
  [DecisionType.acquireOwnSharesPowerOfAttorney]:
    DecisionCategory.powerOfAttorneyFromGeneralMeeting,
};

export type WithDecisionType<T extends DecisionType> = {
  type: T;
};

export type DecisionItem = {
  decisionId: string;
  type: DecisionType;
  title: TMessage;
  decisionTexts: LocalizedTextForDocument[];
  attachments: ProjectDecisionAttachment[];
  decisionData?: DecisionData;
  includeInGeneralMeeting?: boolean;
  defaultGeneralMeetingVotingType: GeneralMeetingVotingType;
  isApprovedByGeneralMeeting: boolean;
  hasValidationError?: boolean;
};

export type DecisionStatusResponse = {
  followUps: Record<string, DecisionFollowUp>;
};

export type BRSigner = {
  name: string;
  role: BRSignerRole;
  email?: string;
};

export enum BRSignerRole {
  ceo = "ceo",
  chairperson = "chairperson",
  other = "other",
}

export enum FollowUpStatus {
  READY = "READY",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

export enum FollowUpType {
  viewSubscribers = "viewSubscribers",
  subscriptionForm = "subscriptionForm",
  subscriptionFormStatus = "subscriptionFormStatus",
  allocatedShares = "allocatedShares",
  attachment = "attachment",
  identification = "identification",
  sendToBR = "sendToBR",
  apply = "apply",
}

export type DecisionFollowUp = {
  uploadedDecisionAttachments: DecisionAttachment[];
  followUps: FollowUp[];
  orgNo: string;
  isEnabled: boolean;
};

export type FollowUp = {
  data: FollowUpData;
  status: FollowUpStatus;
  isActive: boolean;
  isForInformation: boolean;
};

export function isFollowUp(x: unknown): x is FollowUp {
  return String(get(x, "data.type")) in FollowUpType;
}

export type FollowUpData =
  | ViewSubscribersFollowUp
  | SubscriptionFormFollowUp
  | SubscriptionFormStatusFollowUp
  | AllocatedSharesFollowUp
  | AttachmentFollowUp
  | IdentificationFollowUp
  | SendToBRFollowUp
  | ApplyFollowUp;
export type ViewSubscribersFollowUp = {
  type: FollowUpType.viewSubscribers;
  numberOfSubscribers: number;
};
export type SubscriptionFormFollowUp = { type: FollowUpType.subscriptionForm; documentId: string };
export type SubscriptionFormStatusFollowUp = {
  type: FollowUpType.subscriptionFormStatus;
  documentId: string;
};
export type AllocatedSharesFollowUp = { type: FollowUpType.allocatedShares };
export type AttachmentFollowUp = {
  type: FollowUpType.attachment;
  attachmentType: AttachmentType;
  requiresEmail: boolean;
  email?: string;
};
export type IdentificationFollowUp = {
  type: FollowUpType.identification;
  name?: string;
  ssn?: string;
};
export type SendToBRFollowUp = {
  type: FollowUpType.sendToBR;
  brSigners: BRSigner[];
  brStatus: BR_STATUS;
  brPublishingDate?: string;
  updatedDate?: string;
};
export type ApplyFollowUp = {
  type: FollowUpType.apply;
  isRightsFromSubscription: boolean;
};

export function canApplyDecision(followUps: FollowUp[]) {
  return followUps.some((followUp) => followUp.data.type === FollowUpType.apply);
}

export function isDecisionApplied(followUps: FollowUp[]) {
  return followUps.some(
    (followUp) =>
      followUp.data.type === FollowUpType.apply && followUp.status === FollowUpStatus.COMPLETED,
  );
}

export enum AttachmentType {
  // noinspection JSUnusedGlobalSymbols
  CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS = "CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS",
  CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS = "CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS",
  BOARD_MEMBERS_ELECTION_IDENTIFICATIONS = "BOARD_MEMBERS_ELECTION_IDENTIFICATIONS",
  EMPLOY_CEO_IDENTIFICATION = "EMPLOY_CEO_IDENTIFICATION",
  PROCURA_BY_NAME_OTHERS_IDENTIFICATION = "PROCURA_BY_NAME_OTHERS_IDENTIFICATION",
  POA = "POA",
  ATTACHMENT_IN_DECISION = "ATTACHMENT_IN_DECISION",
  UPLOADED_FILE = "UPLOADED_FILE",
  BR_STATUS = "BR_STATUS",
}

export function requiredAttachmentLabel(t: AttachmentType): TMessage | undefined {
  switch (t) {
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS" };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS" };
    case AttachmentType.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS:
      return { key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS" };
    case AttachmentType.EMPLOY_CEO_IDENTIFICATION:
      return { key: "dbot.requiredAttachments.EMPLOY_CEO_IDENTIFICATION" };
    case AttachmentType.PROCURA_BY_NAME_OTHERS_IDENTIFICATION:
      return { key: "dbot.requiredAttachments.PROCURA_BY_NAME_OTHERS_IDENTIFICATION" };
  }
}

export function isIdentificationAttachment(attachmentType: AttachmentType) {
  switch (attachmentType) {
    case AttachmentType.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS:
    case AttachmentType.EMPLOY_CEO_IDENTIFICATION:
    case AttachmentType.PROCURA_BY_NAME_OTHERS_IDENTIFICATION:
      return true;
    default:
      return false;
  }
}

export type DecisionAttachment = {
  id: string;
  decisionId: string;
  type: AttachmentType;
  name: string;
};

export type NumberOfSharesForShareType = {
  shareTypeId: number;
  shareTypeCode?: ShareTypeCode;
  numberOfShares: number;
};

export type SharesForShareholder = {
  shareholderId?: number;
  subscriberId?: string;
  shareTypeId: number;
  shareTypeCode?: ShareTypeCode;
  numberOfShares: number;
};

export function ensureShareTypeCodeInSharesForShareTypes(
  shareTypes: ShareType[],
  shares?: NumberOfSharesForShareType[],
): NumberOfSharesForShareType[] {
  return (
    shares?.map((share) => {
      return {
        shareTypeId: share.shareTypeId,
        shareTypeCode:
          share.shareTypeCode ??
          shareTypes.find((shareType) => shareType.sharetypeId === share.shareTypeId)
            ?.sharetypeCode,
        numberOfShares: share.numberOfShares,
      };
    }) ?? []
  );
}

export function total(shares: NumberOfSharesForShareType[]) {
  return shares.reduce((a, i) => a + i.numberOfShares, 0);
}

export type Evaluation = {
  shareNominalValue: number;
  shareForClasses: SharesForClass[];
  articlesOfAssociation: ArticlesOfAssociation;
  company: Company;
  shareTypes: ShareType[];
  sharesForShareholders: SharesForShareholder[];
  roles: CompanyRole[];
};

export type InputState = {
  decisionId: string;
  numberOfShares: NumberOfSharesForShareType[];
  sharesForShareholders: SharesForShareholder[];
  sharenominalValue: number;
  auditorOrgNo: string;
  articles?: ArticlesOfAssociation;
};

export type ProjectInitializationParams = {
  user: User;
  companyInfo: CompanyInfo;
  shareholderData: ShareholderData;
  currentSubscription: PaidSubscription | undefined;
  fileUploadConfig: FileUploadConfig;
  aiClient: AIClient;
  rootDispatch: EventDispatcher;
  rootStoreData: RootStoreData;
  companies: ProjectCompany[];
};

export type RecipientId = number | string;

export type Recipient = {
  id: RecipientId;
  name: string;
  email: string;
  att?: string;
  ref?: string;
};

export type Subscriber = {
  shareholderId: number;
  subscriberId: string;
  name: string;
  email: string;
};

export interface ProjectContext {
  user: User;
  companyInfo: CompanyInfo;
  shareholderData: ShareholderData;
  currentSubscription: PaidSubscription | undefined;
  fileUploadConfig: FileUploadConfig;
  currentDecisions: DecisionItem[];
  currentDocuments: DocumentItem[];
  documentStatuses: ProjectDocumentStatus[];
  documentLanguages: Language[];
  inputStates: InputState[];
  isLocked: boolean;
  hasSavedDefaultDocumentLanguage: boolean;
  boardMeetingFields: BoardMeetingFields;
  generalMeetingFields: GeneralMeetingFields;
  projectRoles: ProjectCompanyRole[];
  orgNo: string;
  projectId: string;
  projectStatus: ProjectStatus;
  subscriptionTypeModel: SubscriptionTypeModel;
  boardMeetingNoticeRecipients: Recipient[];
  boardMeetingMinutesParticipants: Recipient[];
  signingBoardMembers: CompanyRoleIdWithName[];
  aiClient: AIClient;
  isSharepointEnabled: boolean;
  locale: Language;
  detailsPanelMaximized: boolean;
  boardMeetingVotes: BoardMeetingVote[];
  validationErrors?: TMessage[];
  displayMode: "normal" | "fullscreen" | "screen-share" | "overlay";
  participationList: GeneralMeetingParticipation[];
  powerOfAttorneys: PowerOfAttorneyForGeneralMeeting[];
  companies: ProjectCompany[];
  companyContexts: { [orgNo: string]: Result<ProjectCompanyContext> | undefined };
  batchData: GetProjectResponse["batchData"];
  isBatchProject: GetProjectResponse["isBatchProject"];
  documentSigners: Map<string, Result<GetProjectDocumentSignersResponse>>;
  userPrefs: UserPrefs;
  state: ProjectControllerState;
  jsonData: ProjectJsonData;
  isProd: boolean;
  featureToggles: FeatureToggles;
  subscribers: Subscriber[];
  getData: (req: DataRequest) => DataResponse | undefined;
  addData: (req: DataRequest) => void;
}

export interface DecisionContext extends ProjectContext {
  decisionId: string;
  inputState?: InputState;
  decisionTexts: LocalizedTextForDocument[];
  attachments: ProjectDecisionAttachment[];
}

export interface DocumentContext<T, TOverrides = DocumentOverrides> extends ProjectContext {
  documentId: string;
  documentData: T;
  documentOverrides: { [orgNo: string]: TOverrides | undefined };
  isProjectLocked: boolean;
  attachments: UploadedAttachment[];
}

export interface DecisionTypeHandler<
  T extends DecisionType,
  TData extends WithDecisionType<T>,
  TForm extends WithDecisionType<T>,
> {
  type: T;
  initNewForm: (
    dispatch: DbotEventDispatcher,
    context: DecisionContext,
    data?: TData,
    decision?: Decision,
  ) => TForm;
  isValid: (form: TForm) => boolean;
  validate: (form: TForm) => boolean;
  applyForm: (form: TForm) => TData;
  handleEvent?: (form: TForm, event: DbotEvent) => void;
  getSubscribers?: (form: TForm) => Subscriber[];
}

///
/// Document
///

export type DocumentItem = {
  documentId: string;
  type: DocumentType;
  title: TMessage;
  languages?: Language[];
};

export type WithDocumentType<T extends DocumentType> = {
  type: T;
};

export type DocumentTypeHandler<
  T extends DocumentType,
  TData extends WithDocumentType<T>,
  TForm extends WithDocumentType<T>,
  TOverrides = DocumentOverrides,
> = {
  type: T;
  initNewForm: (
    dispatch: DbotEventDispatcher,
    context: DocumentContext<TData>,
    options?: Record<string, unknown>,
  ) => TForm;
  initForm: (form: TForm) => CleanupFunction | void;
  applyForm: (form: TForm) => TData;
  applyOverrides?: (form: TForm, orgNo: string) => TOverrides | undefined;
  handleEvent?: (form: TForm, event: DbotEvent) => void;
  getTitle?: (form: TForm, lang: Language) => TMessage | undefined;
  getRecipients?: (form: TForm, orgNo: string) => Recipient[];
};

export type CompanyRoleIdWithName = {
  companyroleId: number;
  name: string;
};

export const toCompanyRoleIdWithName = (role: CompanyRole) => ({
  companyroleId: role.companyroleId,
  name: getFullName(role),
});

export type GetDocumentViewingStatusResponse = {
  statusForViewers: DocumentViewingStatus[];
};

export type DocumentViewingStatus = {
  recipientName: string;
  recipientEmail: string;
  emailStatus?: EmailStatus;
  sendingTime?: string;
  viewedTime?: string;
  companyroleId?: CompanyroleId;
  boardMeetingId?: string;
  shareholderId?: number;
  recipientDocumentId: string;
};

export type ProjectDocumentStatus = {
  documentId: string;
  signingStatus: DocumentSigningStatus;
  numberOfSigners: number;
  numberOfSignatures: number;
  numberOfSignedDocuments: number;
  numberOfViewers: number;
  numberOfViewed: number;
  viewerRecipientIds: string[];
  sentAt?: string;
  signedAt?: string;
  isLocked?: boolean;
  lockedJsonData?: ProjectJsonData;
  authMethod?: MeetingAuthMethod;
  meetingPassword?: string;
  eventLogs: EventLog[];
};

export type SendDocumentsForViewingRequest = {
  organizationnumber: string;
  projectId: string;
  documentTypes: DocumentType[];
  documentId?: string;
  emailBody: string;
  authMethod?: MeetingAuthMethod;
  password?: string;
  recipients?: Recipient[];
};

export type MeetingAuthMethod = "LOGIN" | "PASSWORD";

export const sendDocumentsForViewingRequest = (
  data: SendDocumentsForViewingRequest,
): SendDocumentsForViewingRequest => data;

export type ResendNoticeOfBoardMeetingRequest = {
  organizationnumber: string;
  projectId: string;
  emailBody: string;
};
export const resendNoticeOfBoardMeetingRequest = (
  data: ResendNoticeOfBoardMeetingRequest,
): ResendNoticeOfBoardMeetingRequest => data;

export function sortDocuments<T extends WithDocumentType<DocumentType>>(documents: T[]): T[] {
  const types = Object.values(DocumentType);
  return sortBy(documents, (d) => types.indexOf(d.type));
}

///
/// Summary
///

export type SendDocumentsForSigningRequest = {
  projectId: string;
  documentId: string;
  emailBody?: string;
  signingSettings?: SigningSettings;
  signers: {
    reference?: string;
    email: string;
    phoneNumber?: string;
    signerName?: string;
    signingMethod: SigningMethod;
    language: Language;
    orgNo?: string;
  }[];
};

export function mapToSendForSigningRequest(
  projectId: string,
  event: SendDocumentForSigningEvent | SendSubscriptionFormForSigningEvent,
  signingSettings?: SigningSettings,
  emailBody?: string,
): SendDocumentsForSigningRequest {
  return {
    projectId,
    documentId: event.document.documentId,
    signingSettings,
    emailBody: emailBody,
    signers: event.signers.map((s) => ({
      reference: s.reference,
      email: s.email,
      phoneNumber: s.phoneNumber ? formatPhoneNumberToE164(s.phoneNumber) : undefined,
      signerName: s.name,
      signingMethod: s.signingMethod,
      signerType: s.signerType,
      language: s.language,
      orgNo: s.orgNo,
    })),
  };
}

///
/// Autosuggesting AoA paragraph number
///

export type SectionCategory = "COMPANYNAME" | "SIGNATURE" | "BOARD" | "SHARE_CAPITAL";

export function extractSectionWithMaxScoreFromArticles(
  items: ExploreArticlesOfAssociationSearchResultItem[],
  categoryName: SectionCategory,
): number | undefined {
  const item = items.find((i) => i.category === categoryName);
  const max = maxBy(item?.articles, (a) => a.score);
  return max?.clause;
}

export type RemovalMode = RemoveParagraph | UpdateParagraph;

export type RemoveParagraph = {
  type: "RemoveParagraph";
};

export type UpdateParagraph = {
  type: "UpdateParagraph";
  newTexts: LocalizedText[];
};

export type ArticlesOfAssociationChangeMode = RemoveArticle | EditArticle | AddArticle;

export type RemoveArticle = {
  type: ArticleChangeType.RemoveArticle;
  id: string;
  paragraph: number;
  titlesBefore: LocalizedText[];
  textsBefore: LocalizedText[];
};

export type EditArticle = {
  type: ArticleChangeType.EditArticle;
  id: string;
  paragraph: number;
  titlesBefore: LocalizedText[];
  textsBefore: LocalizedText[];
  titlesAfter: LocalizedText[];
  textsAfter: LocalizedText[];
};

export type AddArticle = {
  type: ArticleChangeType.AddArticle;
  id: string;
  paragraph: number;
  titlesAfter: LocalizedText[];
  textsAfter: LocalizedText[];
};

///
/// Auditor
///

export type AuditorList = {
  items: AuditorListItem[];
};

export type AuditorListItem = {
  name: string;
  organisationNumber: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

///
///  ExploreArticlesOfAssociation
///

export type ExploreArticlesOfAssociationSearchResult = {
  results: ExploreArticlesOfAssociationSearchResultItem[];
};

//is this a good naming? Probably not! Hence, the JS-doc here.
//we want to search for specific words in the Articles of Association
//and suggest the most pertinent article / chapter to the user.
export type ExploreArticlesOfAssociationSearchResultItem = {
  category: string;
  articles: Array<ClauseScore>;
};

export type ClauseScore = {
  clause: number;
  score: number;
};

///
/// FetchIncreaseOfShareCapitalProperties
///

export type IncreaseOfShareCapitalSubscribersStatus =
  | IncreaseOfShareCapitalSubscribersStatusForNominalValue
  | IncreaseOfShareCapitalSubscribersStatusForIssue;

export type IncreaseOfShareCapitalSubscribersStatusForNominalValue = {
  type: IncreaseOfShareCapitalType.increaseNominalValue;
  contributionPerShareType: ContributionPerShareType[];
  subscribers: SubscriberForIncreaseNominalValue[];
};

export type IncreaseOfShareCapitalSubscribersStatusForIssue = {
  type: IncreaseOfShareCapitalType.shareIssue;
  selectedShareTypes: ShareIssuePerShareType[];
  subscribers: SubscriberForIssue[];
  isPreferentialRights: boolean;
  isOverSubscriptionAllowed: boolean;
  subscriptionPeriod: PeriodForSubscription;
};

export type ShareAllocationRoundsResponse = {
  allocationRounds: ShareAllocationRound[];
  isAllocationCompleted: boolean;
};

export type ShareAllocationRound = {
  id: string;
  projectId: string;
  documentId: string;
  sharetypeId?: number;
  shareTypeCode: ShareTypeCode;
  roundNumber: number;
  minShares: number;
  maxShares: number;
  allocations: ShareAllocation[];
  isCompleted: boolean;
};

export type ShareAllocation = {
  id: string;
  subscriberId: string;
  shareholderId?: number;
  name: string;
  offeredShares: number;
  subscribedShares?: number;
  allocatedShares?: number;
  subscriptionPrice: number;
};

///
/// Financial
///

export const defaultFinancialYear = (): string => toYearISOString(plusYears(new Date(), -1));

///
/// General Meeting Voting
///

export type IndividualVote = "for" | "against" | "blank";

export type GeneralMeetingIndividualVote = {
  id: string;
  shareholderId: number;
  vote: IndividualVote;
};

export const voteLabel = (vote: IndividualVote): TMessage => {
  switch (vote) {
    case "for":
      return { key: "vote.for" };
    case "against":
      return { key: "vote.against" };
    case "blank":
      return { key: "vote.blank" };
  }
};

export const voteLabelUppercase = (vote: IndividualVote): TMessage => {
  switch (vote) {
    case "for":
      return { key: "vote.for.uppercase" };
    case "against":
      return { key: "vote.against.uppercase" };
    case "blank":
      return { key: "vote.blank.uppercase" };
  }
};

export type GeneralMeetingVotesResponse = {
  meetChair: GeneralMeetingIndividualVote[];
  approveNoticeAndAgenda: GeneralMeetingIndividualVote[];
  decisions: {
    [key: string]: GeneralMeetingIndividualVote[];
  };
};

///
/// Freetext
///

export type Freetext = {
  type: DecisionType.freetext;
  localizedTitles: LocalizedText[];
  requiresGeneralMeeting: boolean; // is used to include in the list of matters to be discussed in the GM
  freetextTypeForAllDocuments: FreetextType;
  customizedFreetextTypePerDocument: FreetextTypeForDocument[];
};

export type FreetextTypeChangedEvent = {
  type: "dbot/freetext-type-changed";
  decisionId: string;
  freetextType: FreetextType;
  documentType?: DocumentType; //if freetextType == CUSTOMIZED, this is provided
  customizedFreetextType?: CustomizedFreetextType; //if freetextType == CUSTOMIZED, this is provided
};

export const emptyFreetext: Freetext = {
  type: DecisionType.freetext,
  localizedTitles: [],
  freetextTypeForAllDocuments: FreetextType.INFORMATION_MATTER,
  customizedFreetextTypePerDocument: [],
  requiresGeneralMeeting: false,
};

///
/// Project controller state
///

export type DeleteProjectState = {
  type: "delete-project";
  deleteResult?: Result;
  alert?: AlertMessage;
  onSuccess?: (DbotEvent | DCEvent)[];
};

export type ReadyState = { type: "ready" };

export type RemoveDecisionState = {
  type: "removeDecision";
  decisionId: string;
  onSuccess?: DbotEvent;
};

export type RemoveDocumentState = {
  type: "removeDocument";
  documentId: string;
  isSubmitting?: boolean;
};

export type LoadingState = { type: "loading"; message: TMessage };

export type RemovingDecisionState = {
  type: "removingDecision";
  decisionId: string;
  result: Result;
  onSuccess?: DbotEvent;
};

export type SelectBatchCompaniesState = {
  type: "select-batch-companies";
  self: ProjectCompany;
  model: OptionFieldModel<ProjectCompany>;
  result?: Result;
};

export type RemoveBatchCompanyState = {
  type: "remove-batch-company";
  orgNo: string;
  result?: Result;
};

export type AddFreetextState = {
  type: "add-freetext";
  input: string;
  documentId?: string;
  freetextType: FreetextType;
  isLoading?: boolean;
};

export type DecisionSelectorState = {
  type: "decision-selector";
  selected: DecisionType[];
  documentId?: string;
  isLoading?: boolean;
};

export type CopyDecisionSelectorState = {
  type: "copy-decision-selector";
  projects: Result<ProjectForCopy[]>;
  selectedDecisions: string[];
  selectedProjectIndex: number;
  documentId?: string;
  isLoading?: boolean;
};

export type AddDocumentState = {
  type: "add-document";
  documentOptions: DocumentOption[];
  customDocumentOptions?: DocumentOption[];
  isDisabled?: boolean;
  isLoading?: boolean;
};

export type UploadingAttachmentsState = {
  type: "uploading-attachments";
  reference?: string;
};

export type DeletingAttachmentState = {
  type: "deleting-attachment";
  attachmentId: string;
  reference?: string;
};

export type ProjectControllerState =
  | AddDocumentState
  | AddFreetextState
  | DecisionSelectorState
  | CopyDecisionSelectorState
  | DeleteProjectState
  | DeletingAttachmentState
  | LoadingState
  | ReadyState
  | RemoveBatchCompanyState
  | RemoveDecisionState
  | RemoveDocumentState
  | RemovingDecisionState
  | SelectBatchCompaniesState
  | UploadingAttachmentsState;

export const readyState = (): ReadyState => ({ type: "ready" });

export const removeDecisionState = (
  decisionId: string,
  onSuccess?: DbotEvent,
): RemoveDecisionState => ({
  type: "removeDecision",
  decisionId,
  onSuccess,
});

export const isRemoveDecision = (state: ProjectControllerState): state is RemoveDecisionState =>
  state.type === "removeDecision";

export const removingDecisionState = (state: RemoveDecisionState): RemovingDecisionState => ({
  type: "removingDecision",
  decisionId: state.decisionId,
  result: pending(),
});

export const isRemovingDecision = (state: ProjectControllerState): state is RemovingDecisionState =>
  state.type === "removingDecision";

export const sortedCompanies = (companies: ProjectCompany[]) => sortBy(companies, "name");

export const selectBatchCompaniesState = (context: ProjectContext): SelectBatchCompaniesState => ({
  type: "select-batch-companies",
  self: { orgNo: context.orgNo, name: context.companyInfo.company.name },
  model: new OptionFieldModel({
    options: filter(sortedCompanies(context.companies), (x) => x.orgNo !== context.orgNo),
    selectedIds: Object.keys(context.batchData),
    idFn: (x) => x.orgNo,
    labelFn: (x) => ({ translated: x.name }),
  }),
});

export const selectBatchCompaniesActions = (
  state: SelectBatchCompaniesState,
): { submit: DbotEvent[]; cancel: DbotEvent[] } => ({
  submit: [
    {
      type: "dbot/update-state",
      update: (state) => {
        if (state.type === "select-batch-companies") {
          state.result = pending();
        }
      },
    },
    {
      type: "dbot/save-selected-batch-companies",
      body: { companies: Array.from(state.model.selectedIds) },
      onSuccess: (data) => [
        { type: "dbot/change-state", state: readyState() },
        { type: "dbot/set-project-data", data },
        { type: "dbot/open-tab", tab: "companies" },
      ],
      onFailure: [
        { type: "dbot/change-state", state: readyState() },
        { type: "show-alert", messageType: "error", message: { key: "general-error" } },
      ],
    },
  ],
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

export const removeBatchCompanyState = (orgNo: string): ProjectControllerState => ({
  type: "remove-batch-company",
  orgNo,
});

export const removeBatchCompanyActions = (
  state: RemoveBatchCompanyState,
  context: ProjectContext,
): { submit: DbotEvent[]; cancel: DbotEvent[] } => ({
  submit: [
    {
      type: "dbot/update-state",
      update: (state) => {
        if (state.type === "remove-batch-company") {
          state.result = pending();
        }
      },
    },
    {
      type: "dbot/save-selected-batch-companies",
      body: { companies: without(Object.keys(context.batchData), state.orgNo) },
      onSuccess: (data) => [
        { type: "dbot/change-state", state: readyState() },
        { type: "dbot/set-project-data", data },
        { type: "dbot/close-details-panel" },
      ],
      onFailure: [
        { type: "dbot/change-state", state: readyState() },
        { type: "show-alert", messageType: "error", message: { key: "general-error" } },
      ],
    },
  ],
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

export const canAddBatchCompany = ({
  currentDecisions,
}: {
  currentDecisions: DecisionItem[];
}): boolean => currentDecisions.every((d) => enabledBatchDecisionTypes.includes(d.type));

export const getNotSupportedDecisionNames = ({
  currentDecisions,
}: {
  currentDecisions: DecisionItem[];
}): TMessage[] =>
  currentDecisions
    .filter((d) => !enabledBatchDecisionTypes.includes(d.type))
    .map((d) => getDecisionName(d.type));

export const addFreetextState = (data: Partial<AddFreetextState>): AddFreetextState => ({
  ...data,
  type: "add-freetext",
  input: data.input ?? "",
  freetextType: data.freetextType ?? FreetextType.INFORMATION_MATTER,
});

export const addFreetextTypeOptions: { value: FreetextType; label: TMessage; icon: IconName }[] = [
  {
    value: FreetextType.INFORMATION_MATTER,
    label: { key: "freetext.informationMatter" },
    icon: "chats",
  },
  {
    value: FreetextType.DECISION_MATTER,
    label: { key: "freetext.decisionMatter" },
    icon: "gavel",
  },
];

export const selectedFreetextTypeOption = (
  state: AddFreetextState,
): { value: FreetextType; label: TMessage; icon: IconName } =>
  addFreetextTypeOptions.find(({ value }) => value === state.freetextType) ??
  addFreetextTypeOptions[0];

export const addFreetextActions = (
  state: AddFreetextState,
): {
  onChange: (event: ChangeEvent<HTMLInputElement>) => DbotEvent[];
  selectFreetextType: (option: { value: FreetextType }) => DbotEvent[];
  submit?: DbotEvent[];
  cancel: DbotEvent[];
} => ({
  onChange: (event) => [
    {
      type: "dbot/change-state",
      state: addFreetextState({ ...state, input: event.target.value }),
    },
  ],
  selectFreetextType: ({ value }: { value: FreetextType }) => [
    {
      type: "dbot/change-state",
      state: addFreetextState({ ...state, freetextType: value }),
    },
  ],
  submit: when(!state.isLoading && state.input?.trim(), (val) => [
    {
      type: "dbot/change-state",
      state: addFreetextState({ ...state, isLoading: true }),
    },
    {
      type: "dbot/add-decisions",
      decisions: [
        { decisionType: DecisionType.freetext, title: val, freetextType: state.freetextType },
      ],
      options: { documentId: state.documentId },
      onSuccess: [
        {
          type: "dbot/change-state",
          state: addFreetextState({ ...state, isLoading: false, input: "" }),
        },
      ],
      onFailure: [
        {
          type: "dbot/change-state",
          state: addFreetextState({ ...state, isLoading: false, input: "" }),
        },
      ],
    },
  ]),
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

export const decisionSelectorState = ({
  selected,
  documentId,
}: {
  selected?: DecisionType[];
  documentId?: string;
}): DecisionSelectorState => ({ type: "decision-selector", selected: selected ?? [], documentId });

export const prepDecisionSelectorViewState = (
  state: DecisionSelectorState,
  context: {
    isBatchProject: boolean;
    orgNo: string;
    currentSubscription: PaidSubscription | undefined;
    currentDecisions: DecisionItem[];
  },
) => ({
  decisionsByCategory: getDecisionsByCategory({
    orgNo: context.orgNo,
    isBatchProject: context.isBatchProject,
    paidSubscription: when(context.currentSubscription, (subscription) => ({
      ...subscription,
      organisationNumber: context.orgNo,
    })),
    current: context.currentDecisions.map((d) => d.type).filter((d) => d !== DecisionType.freetext),
    checkedDecisions: state.selected,
  }),
});

export const decisionSelectorActions = (
  state: DecisionSelectorState,
): {
  toggle: (decisionType: DecisionType) => DbotEvent[];
  submit?: DbotEvent[];
  cancel: DbotEvent[];
} => ({
  toggle: (decisionType) => [
    {
      type: "dbot/change-state",
      state: {
        ...state,
        selected: state.selected.includes(decisionType)
          ? without(state.selected, decisionType)
          : [...state.selected, decisionType],
      },
    },
  ],
  submit:
    state.selected.length && !state.isLoading
      ? [
          { type: "dbot/change-state", state: { ...state, isLoading: true } },
          {
            type: "dbot/add-decisions",
            decisions: state.selected.map((decisionType) => ({ decisionType })),
            options: { documentId: state.documentId },
            onSuccess: [{ type: "dbot/change-state", state: readyState() }],
            onFailure: [{ type: "dbot/change-state", state: readyState() }],
          },
        ]
      : undefined,
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

const copyDecisionProjectId = "copyDecisionsProjectId";

export function openCopyDecisionSelector(projectId: string, documentId?: string): DbotEvent[] {
  const selectedProjectId = sessionStorage.getItem(copyDecisionProjectId);
  const state = copyDecisionSelectorState({ documentId });
  return [
    {
      type: "dbot/change-state",
      state: state,
    },
    {
      type: "dbot/fetch-projects-for-copy",
      projectId,
      onSuccess: (projects) => {
        const selectedProjectIndex = projects.findIndex(
          (project) => project.projectId === selectedProjectId,
        );
        return compact([
          {
            type: "dbot/change-state",
            state: {
              ...state,
              projects: ok(projects),
              selectedProjectIndex: selectedProjectIndex >= 0 ? selectedProjectIndex : 0,
            },
          },
        ]);
      },
      onFailure: [
        {
          type: "dbot/change-state",
          state: { ...state, projects: failure() },
        },
      ],
    },
  ];
}

export const copyDecisionSelectorState = ({
  documentId,
}: {
  documentId?: string;
}): CopyDecisionSelectorState => ({
  type: "copy-decision-selector",
  selectedDecisions: [],
  selectedProjectIndex: 0,
  projects: pending(),
  documentId,
});

export function prepCopyDecisionSelectorViewState(
  state: CopyDecisionSelectorState,
  context: {
    projectId: string;
    isBatchProject: boolean;
    orgNo: string;
    currentSubscription: PaidSubscription | undefined;
    currentDecisions: DecisionItem[];
  },
) {
  const projects = isOk(state.projects) ? state.projects.data : [];
  const selectedProject = projects.length > 0 ? projects[state.selectedProjectIndex] : undefined;

  const enabledDecisionTypes = getEnabledDecisionTypes({ isBatchProject: context.isBatchProject });
  return {
    projects: projects,
    selectedProject: selectedProject,
    decisions:
      selectedProject?.decisions.map((decision) => {
        return {
          decision: decision,
          isSelected: state.selectedDecisions.includes(decision.decisionId),
          isDisabled: !enabledDecisionTypes.includes(decision.decisionType),
          isExisting: context.currentDecisions.some(
            (current) =>
              current.type === decision.decisionType &&
              decision.decisionType !== DecisionType.freetext,
          ),
        };
      }) ?? [],
  };
}

export const copyDecisionSelectorActions = (
  state: CopyDecisionSelectorState,
): {
  toggle: (decisionId: string) => DbotEvent[];
  toggleAll: (decisions: string[]) => DbotEvent[];
  setSelectedProject: (index: number) => DbotEvent[];
  submit?: DbotEvent[];
  cancel: DbotEvent[];
} => ({
  toggle: (decisionId) => [
    {
      type: "dbot/change-state",
      state: {
        ...state,
        selectedDecisions: state.selectedDecisions.includes(decisionId)
          ? without(state.selectedDecisions, decisionId)
          : [...state.selectedDecisions, decisionId],
      },
    },
  ],
  toggleAll: (decisions) => [
    {
      type: "dbot/change-state",
      state: {
        ...state,
        selectedDecisions: decisions,
      },
    },
  ],
  setSelectedProject: (index) => {
    return [
      {
        type: "dbot/change-state",
        state: {
          ...state,
          selectedProjectIndex: index,
        },
      },
    ];
  },
  submit:
    state.selectedDecisions.length &&
    isOk(state.projects) &&
    state.projects.data.length > state.selectedProjectIndex &&
    !state.isLoading
      ? [
          { type: "dbot/change-state", state: { ...state, isLoading: true } },
          {
            type: "dbot/copy-decisions",
            projectId: state.projects.data[state.selectedProjectIndex].projectId,
            decisions: state.selectedDecisions,
            options: { documentId: state.documentId },
            onSuccess: [
              { type: "dbot/change-state", state: readyState() },
              {
                type: "update-session-storage",
                key: copyDecisionProjectId,
                val: state.projects.data[state.selectedProjectIndex].projectId,
              },
            ],
            onFailure: [{ type: "dbot/change-state", state: readyState() }],
          },
        ]
      : undefined,
  cancel: [{ type: "dbot/change-state", state: readyState() }],
});

export type DocumentOption = {
  type: DocumentType;
  label: TMessage;
  customDocumentType?: CustomDocumentType;
};

export function getDocumentOptions(currentDocuments: DocumentType[]): DocumentOption[] {
  return compact([
    DocumentType.notice_board_meeting,
    DocumentType.board_meeting,
    DocumentType.notice_general_meeting,
    DocumentType.general_meeting,
    DocumentType.articlesofassociation,
  ])
    .filter((t) => !currentDocuments.includes(t))
    .map((t) => ({ type: t, label: getDocumentLabel(t) }));
}

export function getCustomDocumentOptions(
  featureToggles: FeatureToggles | undefined,
): DocumentOption[] | undefined {
  return featureToggles?.customDocument
    ? customDocumentTypes.map<DocumentOption>((customDocumentType) => ({
        type: DocumentType.custom_document,
        customDocumentType,
        label: customDocumentTypeLabels[customDocumentType],
      }))
    : undefined;
}

export const addDocumentState = (
  context: {
    currentDocuments: DocumentItem[];
    isLocked?: boolean;
    state: ProjectControllerState;
    featureToggles?: FeatureToggles;
  },
  data: { isLoading?: boolean } = {},
): AddDocumentState => {
  const documentOptions = getDocumentOptions(context.currentDocuments.map((x) => x.type));
  return {
    type: "add-document",
    documentOptions: documentOptions,
    customDocumentOptions: getCustomDocumentOptions(context.featureToggles),
    isLoading: data.isLoading || (context.state.type === "add-document" && context.state.isLoading),
    isDisabled: context.isLocked || !documentOptions.length,
  };
};

export type AddDocumentActions = {
  submit: (
    documentType: DocumentType,
    options?: {
      customDocumentType?: CustomDocumentType;
      onSuccess?: (params: { documentIds: string[] }) => DbotEvent[];
    },
  ) => DbotEvent[];
};

export const addDocumentActions = (context: {
  currentDocuments: DocumentItem[];
  isLocked?: boolean;
  state: ProjectControllerState;
  orgNo: string;
}): AddDocumentActions => ({
  submit: (documentType, options) => [
    { type: "dbot/change-state", state: addDocumentState(context, { isLoading: true }) },
    {
      type: "dbot/add-documents",
      documentTypes: [documentType],
      customDocumentType: options?.customDocumentType,
      expand: true,
      onSuccess: (params) => [
        { type: "dbot/change-state", state: readyState() },
        ...(options?.onSuccess?.(params) ?? []),
      ],
      onFailure: [{ type: "dbot/change-state", state: readyState() }],
    },
  ],
});

export const removeDocumentAction = (documentId: string): DbotEvent[] => [
  { type: "dbot/change-state", state: { type: "removeDocument", documentId } },
];

export const confirmRemoveDocumentDialogActions = (
  state: RemoveDocumentState,
): { onSubmit: DbotEvent[]; onCancel: DbotEvent[] } => ({
  onSubmit: [
    { type: "dbot/change-state", state: { ...state, isSubmitting: true } },
    {
      type: "dbot/confirm-remove-document",
      documentId: state.documentId,
      onSuccess: [{ type: "dbot/change-state", state: readyState() }],
    },
  ],
  onCancel: [{ type: "dbot/change-state", state: readyState() }],
});

///
/// Events
///

export type ChangeStateEvent = {
  type: "dbot/change-state";
  state: ProjectControllerState;
};

export type UpdateStateEvent = {
  type: "dbot/update-state";
  update: (state: ProjectControllerState) => void;
};

export type SetProjectDataEvent = {
  type: "dbot/set-project-data";
  data: GetProjectResponse;
};

export type ReloadProjectEvent = {
  type: "dbot/reload-project";
};

export type RefreshProjectStatusEvent = {
  type: "dbot/refresh-project-status";
};

export type RefreshProjectStatusResultEvent = {
  type: "dbot/refresh-project-status-result";
  result: Ok | Failure<TMessage>;
};

export const refreshProjectStatusEvent = (): RefreshProjectStatusEvent => ({
  type: "dbot/refresh-project-status",
});

export const refreshProjectStatusSuccessEvent = (): RefreshProjectStatusResultEvent => ({
  type: "dbot/refresh-project-status-result",
  result: ok(undefined),
});

export const refreshProjectStatusFailureEvent = (
  error: TMessage,
): RefreshProjectStatusResultEvent => ({
  type: "dbot/refresh-project-status-result",
  result: failure(error),
});

export type FetchAuditorsEvent = {
  type: "dbot/fetch-auditors";
};
export type FetchAuditorsSuccessEvent = {
  type: "dbot/fetch-auditors-success";
  data: AuditorList;
};
export type FetchAuditorsErrorEvent = {
  type: "dbot/fetch-auditors-error";
  error: TMessage;
};
export type ExploreArticlesOfAssociationEvent = {
  type: "dbot/explore-articles-of-association";
  articles?: ArticlesOfAssociation;
};
export type ExploreArticlesOfAssociationSuccessEvent = {
  type: "dbot/explore-articles-of-association-success";
  data: ExploreArticlesOfAssociationSearchResult;
};
export type ExploreArticlesOfAssociationErrorEvent = {
  type: "dbot/explore-articles-of-association-error";
  error: TMessage;
};

export type ElectCommitteeListEvent = {
  type: "dbot/elect-committee-list";
};

export type ElectCommitteeListResultEvent = {
  type: "dbot/elect-committee-list-result";
  data: Array<SelectableWithId>;
};

export type ElectCommitteeAddedEvent = {
  type: "dbot/elect-committee-added";
  data: SelectableWithId;
};

export type ElectCommitteeRemovedEvent = {
  type: "dbot/elect-committee-removed";
  committeeId: string;
};

export type DecisionOption = {
  decisionType: DecisionType;
  title?: string;
  freetextType?: FreetextType;
};

export type AddDecisionsEvent = {
  type: "dbot/add-decisions";
  decisions: DecisionOption[];
  options?: { index?: number; documentId?: string };
  onSuccess?: DbotEvent[];
  onFailure?: DbotEvent[];
};

export const addDecisionsEvent = (
  decisions: DecisionOption[],
  options?: { index?: number; documentId?: string },
): AddDecisionsEvent => ({ type: "dbot/add-decisions", decisions, options });

export type CopyDecisionsEvent = {
  type: "dbot/copy-decisions";
  projectId: string;
  decisions: string[];
  options?: { index?: number; documentId?: string };
  onSuccess?: (DbotEvent | DCEvent)[];
  onFailure?: (DbotEvent | DCEvent)[];
};

export type AddDocumentsEvent = {
  type: "dbot/add-documents";
  documentTypes: DocumentType[];
  customDocumentType?: CustomDocumentType;
  expand?: boolean;
  onSuccess?: (params: { documentIds: string[] }) => DbotEvent[];
  onFailure?: DbotEvent[];
};

export type DocumentsAddedEvent = {
  type: "dbot/documents-added";
  addedDocumentTypes: DocumentType[];
  existingDocumentTypes: DocumentType[];
};

export const addGeneralMeetingDocuments = (): AddDocumentsEvent => ({
  type: "dbot/add-documents",
  documentTypes: [DocumentType.notice_general_meeting, DocumentType.general_meeting],
});

export const addSubscriptionFormDocument = (): AddDocumentsEvent => ({
  type: "dbot/add-documents",
  documentTypes: [DocumentType.subscription_form],
});

export type RemoveDecisionEvent = {
  type: "dbot/remove-decision";
  decisionId: string;
  onSuccess?: DbotEvent;
};

export type RemoveCustomPanelEvent = {
  type: "dbot/remove-custom-panel";
  id: string;
};

export type GeneralMeetingShortNoticeStatusChangedEvent = {
  type: "dbot/general-meeting-short-notice-status-changed";
  isTooShortNotice: boolean;
  repairTextDeadlineGeneralMeeting: boolean;
};
export const generalMeetingShortNoticeStatusChangedEvent = (data: {
  isTooShortNotice: boolean;
  repairTextDeadlineGeneralMeeting: boolean;
}): GeneralMeetingShortNoticeStatusChangedEvent => ({
  type: "dbot/general-meeting-short-notice-status-changed",
  ...data,
});

export type UpdateInputStateEvent = {
  type: "dbot/update-input-state";
  inputStates: InputState[];
};

export const updateInputStateEvent = (data: {
  inputStates: InputState[];
}): UpdateInputStateEvent => ({
  type: "dbot/update-input-state",
  ...data,
});

export type RegisterNewSignerEvent = {
  type: "dbot/register-new-signer";
  name: string;
  email?: string;
};

export type RegisterNewSignerResultEvent = {
  type: "dbot/register-new-signer-result";
  result: Ok<CompanyRole> | Failure<TMessage>;
};

export const registerNewSignerEvent = (name: string, email?: string): RegisterNewSignerEvent => ({
  type: "dbot/register-new-signer",
  name,
  email,
});

export const registerNewSignerSuccessEvent = (role: CompanyRole): RegisterNewSignerResultEvent => ({
  type: "dbot/register-new-signer-result",
  result: ok(role),
});

export const registerNewSignerFailureEvent = (message: TMessage): RegisterNewSignerResultEvent => ({
  type: "dbot/register-new-signer-result",
  result: failure(message),
});

export type FetchIncreaseOfShareCapitalSubscriptionFormSignersEvent = {
  type: "dbot/fetch-increase-of-share-capital-subscription-form-signers";
};

export type FetchIncreaseOfShareCapitalSubscriptionFormSignersResultEvent = {
  type: "dbot/fetch-increase-of-share-capital-subscription-form-signers-result";
  signers: Signer[];
  decisionId: string;
};

export type FetchIncreaseOfShareCapitalSubscribersStatusEvent = {
  type: "dbot/fetch-increase-of-share-capital-subscribers-status";
};

export type FetchIncreaseOfShareCapitalSubscribersStatusResultEvent = {
  type: "dbot/fetch-increase-of-share-capital-subscribers-status-result";
  data: IncreaseOfShareCapitalSubscribersStatus;
};

export type AddIncreaseOfShareCapitalSubscribersForIssueEvent = {
  type: "dbot/addIncreaseOfShareCapitalSubscribersForIssue";
  subscribers: SubscriberForIssue[];
  signersForAllSubscribers: SubscriberSigner[];
};

export type AddIncreaseOfShareCapitalSubscribersForNominalValueEvent = {
  type: "dbot/addIncreaseOfShareCapitalSubscribersForNominalValue";
  subscribers: SubscriberForIncreaseNominalValue[];
  signersForAllSubscribers: SubscriberSigner[];
};

export type FetchIncreaseOfShareCapitalSubscribersAllocationEvent = {
  type: "dbot/fetch-increase-of-share-capital-subscribers-allocation";
};

export type FetchIncreaseOfShareCapitalSubscribersAllocationResultEvent = {
  type: "dbot/fetch-increase-of-share-capital-subscribers-allocation-result";
  result: Ok<ShareAllocationRoundsResponse> | Failure<TMessage>;
};

export const fetchIncreaseOfShareCapitalSubscribersAllocationSuccessEvent = (
  data: ShareAllocationRoundsResponse,
): FetchIncreaseOfShareCapitalSubscribersAllocationResultEvent => ({
  type: "dbot/fetch-increase-of-share-capital-subscribers-allocation-result",
  result: ok(data),
});

export const fetchIncreaseOfShareCapitalSubscribersAllocationFailureEvent = (
  error: TMessage,
): FetchIncreaseOfShareCapitalSubscribersAllocationResultEvent => ({
  type: "dbot/fetch-increase-of-share-capital-subscribers-allocation-result",
  result: failure(error),
});

export type SaveShareAllocationEvent = {
  type: "dbot/save-share-allocation";
  allocationRounds: ShareAllocationRound[];
  isAllocationCompleted: boolean;
};

export type SaveShareAllocationResultEvent = {
  type: "dbot/save-share-allocation-result";
  result: Ok<boolean> | Failure<TMessage>;
};

export const saveShareAllocationSuccessEvent = (
  isCompleted: boolean,
): SaveShareAllocationResultEvent => ({
  type: "dbot/save-share-allocation-result",
  result: ok(isCompleted),
});

export const saveShareAllocationFailureEvent = (
  error: TMessage,
): SaveShareAllocationResultEvent => ({
  type: "dbot/save-share-allocation-result",
  result: failure(error),
});

export type DownloadSignedDocumentEvent = {
  type: "dbot/download-signed-document";
  documentId: string;
  documentType: DocumentType;
};
export const downloadSignedDocumentEvent = (
  documentId: string,
  documentType: DocumentType,
): DownloadSignedDocumentEvent => ({
  type: "dbot/download-signed-document",
  documentId,
  documentType,
});

export type DownloadSignedSubscriptionForms = {
  type: "dbot/download-signed-subscription-forms";
  documentId: string;
};
export const downloadSignedSubscriptionForms = (
  documentId: string,
): DownloadSignedSubscriptionForms => ({
  type: "dbot/download-signed-subscription-forms",
  documentId,
});

export type ValidateProjectEvent = {
  type: "dbot/validate-project";
  onSuccess: DbotEvent;
};

export type ReopenDocumentEvent = {
  type: "dbot/reopen-document";
  documentId: string;
};

export const reopenDocumentEvent = (documentId: string): ReopenDocumentEvent => ({
  type: "dbot/reopen-document",
  documentId,
});

export type ReopenDocumentResultEvent = {
  type: "dbot/reopen-document-result";
  documentId: string;
  label: string;
  reopenResult: Result;
};

export const reopenDocumentResultEvent = (
  documentId: string,
  result: Result,
): ReopenDocumentResultEvent => ({
  type: "dbot/reopen-document-result",
  documentId,
  label: "reopen.dialog.header",
  reopenResult: result,
});

export type GetSigningStatusEvent = {
  type: "dbot/get-signing-status";
  documentId: string;
};

export const getSigningStatusEvent = (documentId: string): GetSigningStatusEvent => ({
  type: "dbot/get-signing-status",
  documentId,
});

export type GetSigningStatusResultEvent = {
  type: "dbot/get-signing-status-result";
  documentId: string;
  result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>;
};

export const getSigningStatusSuccessEvent = (
  documentId: string,
  data: GetDocumentSigningStatusResponse,
): GetSigningStatusResultEvent => ({
  type: "dbot/get-signing-status-result",
  documentId,
  result: ok(data),
});

export const getSigningStatusFailureEvent = (
  documentId: string,
  error: TMessage,
): GetSigningStatusResultEvent => ({
  type: "dbot/get-signing-status-result",
  documentId,
  result: failure(error),
});

export type GetSubscriptionStatusEvent = {
  type: "dbot/get-subscription-status";
  documentId: string;
};

export const getSubscriptionStatusEvent = (documentId: string): GetSubscriptionStatusEvent => ({
  type: "dbot/get-subscription-status",
  documentId,
});

export type GetSubscriptionStatusResultEvent = {
  type: "dbot/get-subscription-status-result";
  documentId: string;
  result: Ok<GetSubscriptionStatusResponse> | Failure<TMessage>;
};

export const getSubscriptionStatusSuccessEvent = (
  documentId: string,
  data: GetSubscriptionStatusResponse,
): GetSubscriptionStatusResultEvent => ({
  type: "dbot/get-subscription-status-result",
  documentId,
  result: ok(data),
});

export const getSubscriptionStatusFailureEvent = (
  documentId: string,
  error: TMessage,
): GetSubscriptionStatusResultEvent => ({
  type: "dbot/get-subscription-status-result",
  documentId,
  result: failure(error),
});

export type GetViewingStatusEvent = {
  type: "dbot/get-viewing-status";
  documentId: string;
};

export const getViewingStatusEvent = (documentId: string): GetViewingStatusEvent => ({
  type: "dbot/get-viewing-status",
  documentId,
});

export type GetViewingStatusResultEvent = {
  type: "dbot/get-viewing-status-result";
  documentId: string;
  result: Ok<GetDocumentViewingStatusResponse> | Failure<TMessage>;
};

export const getViewingStatusSuccessEvent = (
  documentId: string,
  data: GetDocumentViewingStatusResponse,
): GetViewingStatusResultEvent => ({
  type: "dbot/get-viewing-status-result",
  documentId,
  result: ok(data),
});

export const getViewingStatusFailureEvent = (
  documentId: string,
  error: TMessage,
): GetViewingStatusResultEvent => ({
  type: "dbot/get-viewing-status-result",
  documentId,
  result: failure(error),
});

export type AutosignDocumentEvent = {
  type: "dbot/autosign-document";
  document: DocumentItem;
  label: TMessage;
};

export type AutoapproveSendToBREvent = {
  type: "dbot/autoapprove-send-to-br";
  decisionId: string;
  label: TMessage;
};

export type ResendDocumentForViewing = {
  recipientName: string;
  recipientEmail: string;
  companyroleId?: number;
  boardMeetingId?: string;
  shareholderId?: number;
  recipientDocumentId: string;
};

export type ResendNoticeToUpdatedRecipientEvent = {
  type: "dbot/resend-notice-to-updated-recipient";
  documentId: string;
  data: ResendDocumentForViewing;
};

export type ResendNoticeToUpdatedRecipientResultEvent = {
  type: "dbot/resend-notice-to-updated-recipient-result";
  documentId: string;
  data: ResendDocumentForViewing;
  result: Ok | Failure<TMessage>;
};

export type SendDocumentForViewingEvent = {
  type: "dbot/send-document-for-viewing";
  documentId: string;
  emailBody: string;
  authMethod?: MeetingAuthMethod;
  password?: string;
  recipients?: Recipient[];
  onSuccess?: () => void;
  onFailure?: () => void;
};

export const sendDocumentForViewingEvent = (
  document: DocumentItem,
  emailBody: string,
  authMethod?: MeetingAuthMethod,
  password?: string,
): SendDocumentForViewingEvent => ({
  type: "dbot/send-document-for-viewing",
  documentId: document.documentId,
  emailBody,
  authMethod,
  password,
});

export type SendDocumentForViewingResultEvent = {
  type: "dbot/send-document-for-viewing-result";
  documentId: string;
  result: Ok | Failure<TMessage>;
};

export const sendDocumentForViewingSuccessEvent = (
  documentId: string,
): SendDocumentForViewingResultEvent => ({
  type: "dbot/send-document-for-viewing-result",
  documentId,
  result: ok(),
});

export const sendDocumentForViewingFailureEvent = (
  documentId: string,
  message: TMessage,
): SendDocumentForViewingResultEvent => ({
  type: "dbot/send-document-for-viewing-result",
  documentId,
  result: failure(message),
});

export type ResendNoticeOfBoardMeetingEvent = {
  type: "dbot/resend-notice-of-board-meeting";
  document: DocumentItem;
  emailBody: string;
};

export const resendNoticeOfBoardMeetingEvent = (
  document: DocumentItem,
  emailBody: string,
): ResendNoticeOfBoardMeetingEvent => ({
  type: "dbot/resend-notice-of-board-meeting",
  document,
  emailBody,
});

export type ResendNoticeOfBoardMeetingResultEvent = {
  type: "dbot/resend-notice-of-board-meeting-result";
  document: DocumentItem;
  result: Ok | Failure<TMessage>;
};

export const resendNoticeOfBoardMeetingSuccessEvent = (
  document: DocumentItem,
): ResendNoticeOfBoardMeetingResultEvent => ({
  type: "dbot/resend-notice-of-board-meeting-result",
  document,
  result: ok(),
});

export const resendNoticeOfBoardMeetingFailureEvent = (
  document: DocumentItem,
  message: TMessage,
): ResendNoticeOfBoardMeetingResultEvent => ({
  type: "dbot/resend-notice-of-board-meeting-result",
  document,
  result: failure(message),
});

export type SendSubscriptionFormForSigningEvent = {
  type: "dbot/send-subscription-form-for-signing";
  document: DocumentItem;
  signers: Signer[];
};

export const sendSubscriptionFormForSigningEvent = (
  document: DocumentItem,
  signers: Signer[],
): SendSubscriptionFormForSigningEvent => ({
  type: "dbot/send-subscription-form-for-signing",
  document,
  signers,
});

export type SendSubscriptionFormForSigningResultEvent = {
  type: "dbot/send-subscription-form-for-signing-result";
  document: DocumentItem;
  signers: Signer[];
  result: Ok | Failure;
};
export const sendSubscriptionFormForSigningResult = (
  request: SendSubscriptionFormForSigningEvent,
  result: Ok | Failure,
): SendSubscriptionFormForSigningResultEvent => ({
  ...request,
  type: "dbot/send-subscription-form-for-signing-result",
  result,
});

export type SendDocumentForSigningEvent = {
  type: "dbot/send-document-for-signing";
  document: DocumentItem;
  signers: Signer[];
  emailBody: string;
  signingSettings: SigningSettings;
};

export const sendDocumentForSigningEvent = (
  document: DocumentItem,
  signers: Signer[],
  emailBody: string,
  signingSettings: SigningSettings,
): SendDocumentForSigningEvent => ({
  type: "dbot/send-document-for-signing",
  document,
  signers,
  emailBody,
  signingSettings,
});

export type SendDocumentForSigningResultEvent = {
  type: "dbot/send-document-for-signing-result";
  document: DocumentItem;
  signers: Signer[];
  result: Ok<null> | Failure;
  emailBody: string;
  signingSettings: SigningSettings;
  userPrefs?: UserPrefs;
};

export const sendDocumentForSigningResultEvent = (
  request: SendDocumentForSigningEvent,
  result: Ok<null> | Failure,
  emailBody: string,
  userPrefs?: UserPrefs,
): SendDocumentForSigningResultEvent => ({
  ...request,
  type: "dbot/send-document-for-signing-result",
  emailBody,
  result,
  userPrefs,
});

export type ResendDocumentForSigningEvent = {
  type: "dbot/resend-document-for-signing";
  documentId: string;
  signedDocumentId: string;
  name: string;
  email: string;
  phoneNumber?: PhoneNumber;
  signingMethod: SigningMethod;
  language?: Language;
};

export type ResendDocumentForSigningResultEvent = {
  type: "dbot/resend-document-for-signing-result";
  documentId: string;
  signedDocumentId: string;
  result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>;
};

export type UpdateBoardMeetingRecipientItem = {
  id: string | number;
  email: string;
};
export type UpdateBoardMeetingRecipientsEvent = {
  type: "dbot/update-board-meeting-recipients";
  items: UpdateBoardMeetingRecipientItem[];
};

export const updateBoardMeetingRecipientsEvent = (
  items: UpdateBoardMeetingRecipientItem[],
): UpdateBoardMeetingRecipientsEvent => ({
  type: "dbot/update-board-meeting-recipients",
  items,
});

export type UpdateBoardMeetingRecipientsResultEvent = {
  type: "dbot/update-board-meeting-recipients-result";
  result: Failure<TMessage> | Ok<UpdateBoardMeetingRecipientItem[]>;
};

export const updateBoardMeetingRecipientsSuccessEvent = (
  items: UpdateBoardMeetingRecipientItem[],
): UpdateBoardMeetingRecipientsResultEvent => ({
  type: "dbot/update-board-meeting-recipients-result",
  result: ok(items),
});

export const updateBoardMeetingRecipientsFailureEvent = (
  error: TMessage,
): UpdateBoardMeetingRecipientsResultEvent => ({
  type: "dbot/update-board-meeting-recipients-result",
  result: failure(error),
});

export type UpdateShareholderEmailEvent = {
  type: "dbot/update-shareholder-email";
  shareholderId: number;
  email: string;
  onFulfilled: (result: Result<TMessage, TMessage>, value?: string) => void;
};

export function applyShareholderEmail(
  context: ProjectContext,
  shareholderId: number,
  email: string,
) {
  for (const companyContext of Object.values(context.companyContexts)) {
    if (isOk(companyContext)) {
      const r = companyContext.data.shareholderData.shareholders.find(
        (r) => r.id === shareholderId,
      );
      if (r) {
        r.email = email;
      }
    }
  }
}

export type UpdateCompanyRoleItem = {
  companyroleId: number;
  email: string;
};

export type UpdateCompanyRoleEmailsEvent = {
  type: "dbot/update-company-role-emails";
  orgNo: string;
  items: UpdateCompanyRoleItem[];
};

export const updateCompanyRoleEmailsEvent = (
  orgNo: string,
  items: UpdateCompanyRoleItem[],
): UpdateCompanyRoleEmailsEvent => ({
  type: "dbot/update-company-role-emails",
  orgNo,
  items,
});

export type UpdateCompanyRoleEmailsResultEvent = {
  type: "dbot/update-company-role-emails-result";
  orgNo: string;
  items: UpdateCompanyRoleItem[];
  result: Ok<TMessage> | Failure<TMessage>;
};

export const updateCompanyRoleEmailsSuccessEvent = (
  orgNo: string,
  items: UpdateCompanyRoleItem[],
  message: TMessage,
): UpdateCompanyRoleEmailsResultEvent => ({
  type: "dbot/update-company-role-emails-result",
  orgNo,
  items,
  result: ok(message),
});

export const updateCompanyRoleEmailsFailureEvent = (
  orgNo: string,
  items: UpdateCompanyRoleItem[],
  message: TMessage,
): UpdateCompanyRoleEmailsResultEvent => ({
  type: "dbot/update-company-role-emails-result",
  orgNo,
  items,
  result: failure(message),
});

export function applyCompanyRoleEmails(context: ProjectContext, items: UpdateCompanyRoleItem[]) {
  for (const updated of items) {
    for (const companyContext of Object.values(context.companyContexts)) {
      if (isOk(companyContext)) {
        const r = companyContext.data.companyInfo.roles.find(
          (r) => r.companyroleId === updated.companyroleId,
        );
        if (r) {
          r.email = updated.email;
        }
      }
    }
  }
}

export type UpdateDecisionTextsEvent = {
  type: "dbot/update-decision-texts";
  decisionId: string;
  texts: DecisionText[];
};

export type DecisionText = {
  documentType: DocumentType;
  language: Language;
  text: string;
  translationOption?: TranslationOption;
};

export const updateDecisionText = (
  decisionId: string,
  documentType: DocumentType,
  language: Language,
  text: string | undefined,
  translationOption?: TranslationOption,
): UpdateDecisionTextsEvent => ({
  type: "dbot/update-decision-texts",
  decisionId,
  texts: [
    {
      documentType,
      language,
      translationOption,
      text: text ?? "",
    },
  ],
});

export type FetchDecisionStatusEvent = {
  type: "dbot/fetch-decision-status";
};

export const fetchDecisionStatusEvent = (): FetchDecisionStatusEvent => ({
  type: "dbot/fetch-decision-status",
});

export type FetchDecisionStatusResultEvent = {
  type: "dbot/fetch-decision-status-result";
  data: DecisionStatusResponse;
};

export const fetchDecisionStatusResultEvent = (
  data: DecisionStatusResponse,
): FetchDecisionStatusResultEvent => ({
  type: "dbot/fetch-decision-status-result",
  data,
});

export type UpdateFollowUpStatusEvent = {
  type: "dbot/update-follow-up-status";
  decisionId: string;
  followUpData: FollowUpData;
};

export const updateFollowUpStatusEvent = (
  decisionId: string,
  followUpData: FollowUpData,
): UpdateFollowUpStatusEvent => ({
  type: "dbot/update-follow-up-status",
  decisionId: decisionId,
  followUpData: followUpData,
});

export type UpdateFollowUpStatusResultEvent = {
  type: "dbot/update-follow-up-status-result";
  data: DecisionStatusResponse;
};

export const updateFollowUpStatusResultEvent = (
  data: DecisionStatusResponse,
): UpdateFollowUpStatusResultEvent => ({
  type: "dbot/update-follow-up-status-result",
  data,
});

export type ApplyDecisionEvent = {
  type: "dbot/apply-decision";
  decisionId: string;
};
export const applyDecisionEvent = (decisionId: string): ApplyDecisionEvent => ({
  type: "dbot/apply-decision",
  decisionId,
});

export type ApplyDecisionResultEvent = {
  type: "dbot/apply-decision-result";
  decisionId: string;
  result: Ok | Failure<TMessage>;
};
export const applyDecisionSuccessEvent = (decisionId: string): ApplyDecisionResultEvent => ({
  type: "dbot/apply-decision-result",
  decisionId,
  result: ok(undefined),
});
export const applyDecisionFailureEvent = (
  decisionId: string,
  message: TMessage,
): ApplyDecisionResultEvent => ({
  type: "dbot/apply-decision-result",
  decisionId,
  result: failure(message),
});

export type SendToBREvent = {
  type: "dbot/send-to-br";
  decisionId: string;
  signer: BRSigner;
};

export const sendToBREvent = (decisionId: string, signer: BRSigner): SendToBREvent => ({
  type: "dbot/send-to-br",
  decisionId,
  signer,
});

export type SendToBRResultEvent = {
  type: "dbot/send-to-br-result";
  decisionId: string;
  result: Ok<TMessage> | Failure<TMessage>;
};

export const sendToBRSuccessEvent = (decisionId: string): SendToBRResultEvent => ({
  type: "dbot/send-to-br-result",
  decisionId,
  result: ok({ key: "dbot.summary.isSentToBR" }),
});

export const sendToBRFailureEvent = (
  decisionId: string,
  message: TMessage,
): SendToBRResultEvent => ({
  type: "dbot/send-to-br-result",
  decisionId,
  result: failure(message),
});

export type DecisionAddedEvent = {
  type: "dbot/decision-added";
  decisionId: string;
  isFreetext: boolean;
};

export type DecisionRemovedEvent = {
  type: "dbot/decision-removed";
  decisionId: string;
};

export type PowerOfAttorneyForGeneralMeeting = {
  shareholderId: number;
  poaRecipientName: string;
  poaRecipientEmail: string;
  poaRecipientRole: PoARecipientRole | undefined;
  poaRecipientCompanyRoleId?: number;
  poaRecipientShareholderId?: number;
};

export type SaveParticipationEvent = {
  type: "dbot/save-participation";
  createParticipation: GeneralMeetingParticipation[];
  updateParticipation: GeneralMeetingParticipation[];
};

export type GeneralMeetingParticipation = {
  participationId?: string;
  shareholderId: number;
  isPresent: boolean;
  representedByName?: string;
  representedById?: number;
  registeredBy?: number;
  registeredAt?: string;
};

export type UploadAttachmentsEvent = {
  type: "dbot/upload-attachments";
  files: File[];
  reference: string;
  attachmentType: AttachmentType;
  selectedDocuments?: DocumentType[];
  source: unknown;
  overrideExistingAttachmentType?: boolean;
};

export const uploadAttachmentsEvent = (params: {
  files: File[];
  reference: string;
  attachmentType: AttachmentType;
  source: unknown;
  overrideExistingAttachmentType?: boolean;
  selectedDocuments?: DocumentType[];
}): UploadAttachmentsEvent => ({ type: "dbot/upload-attachments", ...params });

export type UploadAttachmentsResultEvent = {
  type: "dbot/upload-attachments-result";
  reference: string;
  source: unknown;
  selectedDocuments?: DocumentType[];
  result: Ok<UploadedAttachment[]> | Failure;
};

export const uploadAttachmentsSuccess = (
  attachments: UploadedAttachment[],
  reference: string,
  source: unknown,
  selectedDocuments?: DocumentType[],
): UploadAttachmentsResultEvent => ({
  type: "dbot/upload-attachments-result",
  reference,
  source,
  selectedDocuments,
  result: ok(attachments),
});

export const uploadAttachmentsFailure = (
  reference: string,
  source: unknown,
): UploadAttachmentsResultEvent => ({
  type: "dbot/upload-attachments-result",
  reference,
  source,
  result: failure(),
});

export type DeleteAttachmentEvent = {
  type: "dbot/delete-attachment";
  attachmentId: string;
  source: unknown;
  reference?: string;
};

export const deleteAttachmentEvent = (
  attachmentId: string,
  source: unknown,
  reference?: string,
): DeleteAttachmentEvent => ({
  type: "dbot/delete-attachment",
  attachmentId: attachmentId,
  source,
  reference,
});

export type DeleteAttachmentResultEvent = {
  type: "dbot/delete-attachment-result";
  attachmentId: string;
  source?: unknown;
  reference?: string;
};

export const deleteAttachmentSuccessEvent = (
  attachmentId: string,
  source?: unknown,
  reference?: string,
): DeleteAttachmentResultEvent => ({
  type: "dbot/delete-attachment-result",
  attachmentId,
  source,
  reference,
});

export type DownloadAttachmentEvent = {
  type: "dbot/download-attachment";
  attachmentId: string;
};

export type OpenAttachmentEvent = {
  type: "dbot/open-attachment";
  attachmentId: string;
  filename?: string;
};

export type SaveGeneralMeetingVotesEvent = {
  type: "dbot/save-general-meeting-votes";
  voteType: "meetChair" | "approveNoticeAndAgenda" | "decision";
  decisionId: string | null;
  votes: GeneralMeetingIndividualVote[];
};

export type SaveGeneralMeetingVotesResultEvent = {
  type: "dbot/save-general-meeting-votes-result";
  result: "ok" | "error";
};

export const saveGeneralMeetingVotesSuccessEvent = (): SaveGeneralMeetingVotesResultEvent => ({
  type: "dbot/save-general-meeting-votes-result",
  result: "ok",
});

export const saveGeneralMeetingVotesErrorEvent = (): SaveGeneralMeetingVotesResultEvent => ({
  type: "dbot/save-general-meeting-votes-result",
  result: "error",
});

export type UpdateGeneralMeetingEvent = {
  type: "dbot/update-general-meeting";
  meeting: ElectronicGeneralMeeting;
};

export type FetchGeneralMeetingVotesEvent = {
  type: "dbot/fetch-general-meeting-votes";
};

export type FetchGeneralMeetingVotesResultEvent = {
  type: "dbot/fetch-general-meeting-votes-result";
  votes: GeneralMeetingVotesResponse;
};

export const fetchVotingResultEvent = (
  votes: GeneralMeetingVotesResponse,
): FetchGeneralMeetingVotesResultEvent => ({
  type: "dbot/fetch-general-meeting-votes-result",
  votes,
});

export type SearchCompaniesEvent = {
  type: "dbot/search-companies";
  input: string;
  source: unknown;
};

export type SearchCompaniesResultEvent = {
  type: "dbot/search-companies-result";
  result: SuggestedCompany[];
  source: unknown;
};

export const searchCompaniesResultEvent = (
  source: unknown,
  data?: SuggestedCompany[],
): SearchCompaniesResultEvent => ({
  type: "dbot/search-companies-result",
  result: data ?? [],
  source,
});

export type SendForReviewEvent = {
  type: "dbot/send-for-review";
  documentId: string;
  options?: OptionsBeforeSending;
  sendMessageRequest: CreateAndSendMessageRequest;
};

export type SendForReviewResultEvent = {
  type: "dbot/send-for-review-result";
  result: Ok | Failure;
};

export type NavigateToProjectListEvent = {
  type: "dbot/navigate-to-project-list";
};

export type EditProjectNameEvent = {
  type: "dbot/edit-project-name";
};

export type SaveProjectEvent = {
  type: "dbot/save-project";
};

export type SaveProjectSuccessEvent = {
  type: "dbot/save-project-success";
};

export type SaveProjectScheduledEvent = {
  type: "dbot/save-project-scheduled";
};

export type UpdateVotingInGeneralMeetingEvent = {
  type: "dbot/update-voting-in-general-meeting";
  voting: GeneralMeetingVoting;
  voteType: GeneralMeetingVoteType;
  decisionId?: string;
};

export type UpdateAllVotesInGeneralMeetingEvent = {
  type: "dbot/update-all-votes-in-general-meeting";
  votes: GeneralMeetingVotes;
};

export type UpdateVotingTypeInGeneralMeetingEvent = {
  type: "dbot/update-voting-type-in-general-meeting";
  voteType: GeneralMeetingVoteType;
  votingType?: GeneralMeetingVotingType;
  decisionId?: string;
};

export type SaveProjectNameEvent = {
  type: "dbot/save-project-name";
  name: string;
};

export type OpenMobileDocumentPreviewEvent = {
  type: "dbot/open-mobile-document-preview";
};

export type CloseMobileDocumentPreviewEvent = {
  type: "dbot/close-mobile-document-preview";
};

export type DuplicateProjectEvent = {
  type: "dbot/duplicate-project";
  name: string;
};

export type DeleteProjectEvent = {
  type: "dbot/delete-project";
  alert?: AlertMessage;
  onSuccess?: (DbotEvent | DCEvent)[];
};

export type ConfirmDuplicateProjectEvent = {
  type: "dbot/confirm-duplicate-project";
  name: string;
};

export type ConfirmDeleteProjectEvent = {
  type: "dbot/confirm-delete-project";
};

export type CancelDeleteProjectEvent = {
  type: "dbot/cancel-delete-project";
};

export type ToggleFullscreenEvent = {
  type: "dbot/toggle-fullscreen";
};

export type ToggleMeetingScreenShareModeEvent = {
  type: "dbot/toggle-screen-share-mode";
};

export type DbotAction = {
  icon?: IconName;
  label: TMessage;
  event: DbotEvent;
};

export type ElectronicGeneralMeetingStep =
  | { type: "registration" }
  | { type: "choiceOfMeetingChair" }
  | { type: "approveAgenda" }
  | { type: "decision"; decisionId: string };

export type DetailsPanel = {
  type: "decision" | "company" | "custom" | "generalmeeting" | "follow-up";
  id: string;
  documentId?: string;
  followUp?: { item: FollowUp; decisionId: string; attachmentType?: AttachmentType };
  icon: IconName | IconProps;
  heading?: TMessage;
  summaryHeading?: TMessage;
  label?: TMessage;
  hasValidationError?: boolean;
  tag?: TMessage;
  action?: DbotAction;
  customOpenEvent?: DbotEvent;
  isDisabled?: boolean;
  notReady?: boolean;
  infoMessage?: TMessage;
  generalMeetingStep?: ElectronicGeneralMeetingStep;
  canRemove?: boolean;
};

export type DecisionDetailsPanel = {
  type: "decision";
  decisionId: string;
  documentId?: string;
  orgNo?: string;
};

export type CustomDetailsPanel = {
  type: "custom";
  id: string;
  documentId?: string;
};

export type GeneralMeetingDetailsPanel = {
  type: "generalmeeting";
  id: string;
  step: ElectronicGeneralMeetingStep;
};

export type CompanyDetailsPanel = {
  type: "company";
  orgNo: string;
};

export type FollowUpDetailsPanel = {
  type: "follow-up";
  followUpType: FollowUpType;
  decisionId: string;
  attachmentType?: AttachmentType;
};

export type DetailsPanelRef =
  | CompanyDetailsPanel
  | CustomDetailsPanel
  | DecisionDetailsPanel
  | FollowUpDetailsPanel
  | GeneralMeetingDetailsPanel;

export type ShowGeneralMeetingDialogEvent = {
  type: "dbot/show-general-meeting-dialog";
  voteType?: GeneralMeetingVoteType;
  decisionId?: string;
};

export type OpenDetailsPanelEvent = {
  type: "dbot/open-details-panel";
  panel: DetailsPanelRef;
};

export type CloseDetailsPanelEvent = {
  type: "dbot/close-details-panel";
};

export type ToggleMaximizeDetailsPanelEvent = {
  type: "dbot/toggle-maximize-details-panel";
};

export type UpdateElectronicGeneralMeetingStatusEvent = {
  type: "dbot/electronic-general-meeting-update-status";
  projectId: string;
  statusName: StatusName;
  status: GeneralMeetingStatus;
  decisionId?: string;
};

export type UpdateElectronicGeneralMeetingStatusResultEvent = {
  type: "dbot/electronic-general-meeting-update-status-result";
  result: Result<ElectronicGeneralMeeting>;
};

export type TabId = "companies" | "documents" | "follow-up" | "matters" | "meetings";

export type OpenTabEvent = {
  type: "dbot/open-tab";
  tab: TabId;
};

export type ToggleDocumentPanelEvent = {
  type: "dbot/toggle-document-panel";
  documentId: string;
  expanded: boolean;
};

export type ConfirmRemoveDocumentEvent = {
  type: "dbot/confirm-remove-document";
  documentId: string;
  onSuccess: DbotEvent[];
};

///
/// Decision actions
///

export type OpenIncreaseOfShareCapitalSubscribersStatusEvent = {
  type: "dbot/open-increase-of-share-capital-subscribers-status";
  decisionId: string;
  isLocked: boolean;
};

export type OpenIncreaseOfShareCapitalSubscribersAllocationEvent = {
  type: "dbot/open-increase-of-share-capital-subscribers-allocation";
  decisionId: string;
};

export type ApplyDecisionAction = {
  type: "dbot/applyDecisionAction";
  decision: DecisionItem;
};

export const applyDecisionAction = (decision: DecisionItem): ApplyDecisionAction => {
  return {
    decision,
    type: "dbot/applyDecisionAction",
  };
};

export type ConfirmSendToBRAction = {
  type: "dbot/confirm-send-to-br";
  decisionId: string;
  brSigners: BRSigner[];
};

export const confirmSendToBRAction = (
  decisionId: string,
  brSigners: BRSigner[],
): ConfirmSendToBRAction => {
  return {
    decisionId,
    brSigners,
    type: "dbot/confirm-send-to-br",
  };
};

///
/// Document actions
///

export type OpenSubscriptionStatusEvent = {
  type: "dbot/open-subscription-status";
  document: DocumentItem;
  label: TMessage;
};

export type DownloadDocumentAction = {
  type: "dbot/download-document";
  document: DocumentItem;
  label: TMessage;
};

export type DownloadSubscriptionFormsAction = {
  type: "dbot/download-subscription-forms";
  document: DocumentItem;
  label: TMessage;
};

export type OpenSubscriptionFormBatchPreviewDialogAction = {
  type: "dbot/openSubscriptionFormBatchPreviewDialog";
  label: TMessage;
  title: TMessage;
  filePreview: (file: FileToPreview) => FilePreviewProps;
  successAction: DbotEvent;
};

export type OpenReviewDialogAction = {
  type: "dbot/open-review-dialog";
  document: DocumentItem;
  label: TMessage;
  showUnsupportedAttachmentWarning?: boolean;
  isResend?: boolean;
  signers?: Signer[];
  documents?: ProjectDocumentPreview[];
  companies?: CompanyItem[];
  emailBody?: TMessage;
};

export type ProjectValidationFailedEvent = {
  type: "dbot/project-validation-failed";
  validationError: TMessage;
};

export type ValidateProjectAction = {
  type: "dbot/validate-project-action";
  successAction: DbotEvent;
  label: TMessage;
};

export type OpenSigningStatusDialogAction = {
  type: "dbot/open-signing-status-dialog";
  document: DocumentItem;
  label: TMessage;
};

export type OpenChangePermissionDialogAction = {
  type: "dbot/open-change-permission-dialog";
};

export type OpenViewingStatusDialogAction = {
  type: "dbot/open-viewing-status-dialog";
  document: DocumentItem;
  label: TMessage;
};

export type RefreshSigningStatusesAction = {
  type: "dbot/refresh-signing-status";
  document: DocumentItem;
  label: TMessage;
};

export type UnlockDocumentAction = {
  type: "dbot/unlock-document";
  documentId: string;
  label: TMessage;
};

export type UpdateMinutesLiveParticipationEvent = {
  type: "dbot/update-minutes-live-participation";
  participationList: GeneralMeetingParticipation[];
};

export const updateMinutesLiveParticipationEvent = (
  participationList: GeneralMeetingParticipation[],
): UpdateMinutesLiveParticipationEvent => {
  return {
    type: "dbot/update-minutes-live-participation",
    participationList,
  };
};

export type SendForReviewMessageTemplate = {
  subject: TMessage;
  body: TMessage;
  linkText: TMessage;
  senderName: string;
};

export type OpenSendForReviewDialogAction = {
  type: "dbot/open-send-for-review-dialog";
  document: DocumentItem;
  label: TMessage;
  messageTemplate: SendForReviewMessageTemplate;
  recipients?: CompanyRoleIdWithName[];
};

export type MoveDecisionEvent = {
  type: "dbot/move-decision";
  decisionId: string;
  toDecisionId: string;
};

export type SaveSelectedBatchCompaniesEvent = {
  type: "dbot/save-selected-batch-companies";
  body: SelectBatchCompaniesRequest;
  onSuccess: (data: GetProjectResponse) => (DbotEvent | DCEvent)[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type FetchDocumentSignersEvent = {
  type: "dbot/fetch-document-signers";
  documentId: string;
  onSuccess: (data: GetProjectDocumentSignersResponse) => DbotEvent[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type FetchProjectsForCopyEvent = {
  type: "dbot/fetch-projects-for-copy";
  projectId: string;
  onSuccess: (result: ProjectForCopy[]) => (DbotEvent | DCEvent)[];
  onFailure: (DbotEvent | DCEvent)[];
};

export type FetchCompanyContextData = {
  type: "dbot/fetch-company-context-data";
  orgNo: string;
};

export type CloseDialogEvent = { type: "dbot/close-dialog" };

export type SelectCompanyEvent = { type: "dbot/select-company"; orgNo: string };

export type DbotEventDispatcher = (event: DbotEvent | DCEvent | (DbotEvent | DCEvent)[]) => void;

export type DbotEvent =
  | AddDecisionsEvent
  | AddDocumentsEvent
  | AddIncreaseOfShareCapitalSubscribersForIssueEvent
  | AddIncreaseOfShareCapitalSubscribersForNominalValueEvent
  | ApplyDecisionEvent
  | ApplyDecisionAction
  | ApplyDecisionResultEvent
  | AutosignDocumentEvent
  | AutoapproveSendToBREvent
  | CancelDeleteProjectEvent
  | ChangeStateEvent
  | CloseDetailsPanelEvent
  | CloseDialogEvent
  | CloseMobileDocumentPreviewEvent
  | ConfirmDeleteProjectEvent
  | ConfirmDuplicateProjectEvent
  | ConfirmRemoveDocumentEvent
  | ConfirmSendToBRAction
  | CopyDecisionsEvent
  | DecisionAddedEvent
  | DecisionRemovedEvent
  | DeleteAttachmentEvent
  | DeleteAttachmentResultEvent
  | DeleteProjectEvent
  | DocumentsAddedEvent
  | DownloadAttachmentEvent
  | DownloadDocumentAction
  | DownloadSubscriptionFormsAction
  | DownloadSignedDocumentEvent
  | DownloadSignedSubscriptionForms
  | DuplicateProjectEvent
  | EditProjectNameEvent
  | ElectCommitteeAddedEvent
  | ElectCommitteeListEvent
  | ElectCommitteeListResultEvent
  | ElectCommitteeRemovedEvent
  | ExploreArticlesOfAssociationEvent
  | ExploreArticlesOfAssociationErrorEvent
  | ExploreArticlesOfAssociationSuccessEvent
  | FetchAuditorsEvent
  | FetchAuditorsErrorEvent
  | FetchAuditorsSuccessEvent
  | FetchCompanyContextData
  | FetchDecisionStatusEvent
  | FetchDecisionStatusResultEvent
  | FetchDocumentSignersEvent
  | FetchGeneralMeetingVotesEvent
  | FetchGeneralMeetingVotesResultEvent
  | FetchIncreaseOfShareCapitalSubscribersAllocationEvent
  | FetchIncreaseOfShareCapitalSubscribersAllocationResultEvent
  | FetchIncreaseOfShareCapitalSubscribersStatusEvent
  | FetchIncreaseOfShareCapitalSubscribersStatusResultEvent
  | FetchIncreaseOfShareCapitalSubscriptionFormSignersEvent
  | FetchIncreaseOfShareCapitalSubscriptionFormSignersResultEvent
  | FetchProjectsForCopyEvent
  | FreetextTypeChangedEvent
  | GeneralMeetingShortNoticeStatusChangedEvent
  | GetSigningStatusEvent
  | GetSigningStatusResultEvent
  | GetSubscriptionStatusEvent
  | GetSubscriptionStatusResultEvent
  | GetViewingStatusEvent
  | GetViewingStatusResultEvent
  | ToggleMaximizeDetailsPanelEvent
  | MoveDecisionEvent
  | NavigateToProjectListEvent
  | OpenAttachmentEvent
  | OpenSubscriptionFormBatchPreviewDialogAction
  | OpenDetailsPanelEvent
  | OpenIncreaseOfShareCapitalSubscribersAllocationEvent
  | OpenIncreaseOfShareCapitalSubscribersStatusEvent
  | OpenMobileDocumentPreviewEvent
  | OpenReviewDialogAction
  | OpenSendForReviewDialogAction
  | OpenSigningStatusDialogAction
  | OpenChangePermissionDialogAction
  | OpenSubscriptionStatusEvent
  | OpenTabEvent
  | OpenViewingStatusDialogAction
  | ProjectValidationFailedEvent
  | RefreshProjectStatusEvent
  | RefreshProjectStatusResultEvent
  | RefreshSigningStatusesAction
  | RegisterNewSignerEvent
  | RegisterNewSignerResultEvent
  | ReloadProjectEvent
  | RemoveDecisionEvent
  | RemoveCustomPanelEvent
  | ReopenDocumentEvent
  | ReopenDocumentResultEvent
  | ResendDocumentForSigningEvent
  | ResendDocumentForSigningResultEvent
  | ResendNoticeOfBoardMeetingEvent
  | ResendNoticeOfBoardMeetingResultEvent
  | ResendNoticeToUpdatedRecipientEvent
  | ResendNoticeToUpdatedRecipientResultEvent
  | SaveGeneralMeetingVotesEvent
  | SaveGeneralMeetingVotesResultEvent
  | SaveParticipationEvent
  | SaveProjectEvent
  | SaveProjectNameEvent
  | SaveProjectScheduledEvent
  | SaveProjectSuccessEvent
  | SaveSelectedBatchCompaniesEvent
  | SaveShareAllocationEvent
  | SaveShareAllocationResultEvent
  | SearchCompaniesEvent
  | SearchCompaniesResultEvent
  | SelectCompanyEvent
  | SendDocumentForSigningEvent
  | SendDocumentForSigningResultEvent
  | SendDocumentForViewingEvent
  | SendDocumentForViewingResultEvent
  | SendForReviewEvent
  | SendForReviewResultEvent
  | SendSubscriptionFormForSigningEvent
  | SendSubscriptionFormForSigningResultEvent
  | SendToBREvent
  | SendToBRResultEvent
  | SetProjectDataEvent
  | ToggleDocumentPanelEvent
  | ToggleFullscreenEvent
  | ToggleMeetingScreenShareModeEvent
  | UnlockDocumentAction
  | UpdateBoardMeetingRecipientsEvent
  | UpdateBoardMeetingRecipientsResultEvent
  | UpdateCompanyRoleEmailsEvent
  | UpdateCompanyRoleEmailsResultEvent
  | UpdateDecisionTextsEvent
  | UpdateElectronicGeneralMeetingStatusEvent
  | UpdateElectronicGeneralMeetingStatusResultEvent
  | UpdateFollowUpStatusEvent
  | UpdateFollowUpStatusResultEvent
  | UpdateGeneralMeetingEvent
  | UpdateInputStateEvent
  | UpdateMinutesLiveParticipationEvent
  | UpdateShareholderEmailEvent
  | UpdateStateEvent
  | UpdateVotingInGeneralMeetingEvent
  | UpdateAllVotesInGeneralMeetingEvent
  | UpdateVotingTypeInGeneralMeetingEvent
  | UploadAttachmentsEvent
  | UploadAttachmentsResultEvent
  | ValidateProjectEvent
  | ValidateProjectAction
  | ShowGeneralMeetingDialogEvent;

export const isDbotEvent = (event: object): event is DbotEvent =>
  "type" in event && String(event.type).startsWith("dbot/");

export function mapLocalesToDocumentLanguageWithDefault(
  orgNo: string,
  locales?: Language[],
): DocumentLanguage {
  if (locales?.length === 0) {
    return defaultLanguageForOrgNo(orgNo);
  } else {
    return mapLocalesToDocumentLanguage(locales ?? []);
  }
}

export function mapLocalesToDocumentLanguage(locales: Language[] = []): DocumentLanguage {
  if (locales.includes(Language.NO) && locales.includes(Language.EN)) {
    return "no/en";
  } else if (locales.includes(Language.SV) && locales.includes(Language.EN)) {
    return "sv/en";
  } else if (locales.includes(Language.EN)) {
    return "en";
  } else if (locales.includes(Language.SV)) {
    return "sv";
  }
  return "no";
}

export function mapDocumentLanguageToLocales(documentLanguage: DocumentLanguage) {
  switch (documentLanguage) {
    case "no":
      return [Language.NO];
    case "en":
      return [Language.EN];
    case "sv":
      return [Language.SV];
    case "no/en":
      return [Language.NO, Language.EN];
    case "sv/en":
      return [Language.SV, Language.EN];
  }
}

export const defaultDocumentLanguage = (orgNo: string): Language[] => [
  defaultLanguageForOrgNo(orgNo),
];

///
/// Initialization
///

export type CleanupFunction = () => void;

export interface WithInitialization {
  init: () => CleanupFunction;
}

export interface WithAttachmentSupport {
  getUploadedAttachment: () => [string, UploadedAttachment][];
  uploadAttachments: (files: File[]) => void;
  removeAttachment: (attachmentId: string) => void;
  deleteAttachmentById: (attachmentId: string) => void;
  fileUploadConfig: FileUploadConfig;
}

///
/// Decision texts
///

export type DecisionTextModel = {
  model: LocalizedTextEditorModel;
  decisionId: string;
  title: TMessage;
};

export function mapDecisionTextModel(
  dispatch: (event: DbotEvent) => void,
  decision: DecisionItem,
  documentType: DocumentType,
  context: ProjectContext,
  documentLanguages: Language[],
): DecisionTextModel {
  const dispatchTextChange = (
    translationOption: TranslationOption,
    locale: Language,
    text?: string,
  ) => {
    dispatch(
      updateDecisionText(decision.decisionId, documentType, locale, text, translationOption),
    );
  };

  const model = new LocalizedTextEditorModel({
    isReadOnly() {
      return context.isLocked;
    },
    label: {
      key: "decisions.decisionTextIn",
      params: { document: getDocumentLabel(documentType) },
    },
    texts: documentLanguages.map<LocalizedText>((language) => {
      const decisionText = decision.decisionTexts.find(
        (t) => t.documentType === documentType && t.language === language,
      );
      return {
        text: decisionText?.text ?? "",
        language,
        translationOption: decisionText?.translationOption,
      };
    }),
    onChange: dispatchTextChange,
    aiClient: context.aiClient,
  });
  return {
    model,
    decisionId: decision.decisionId,
    title: model.label,
  };
}

export function mapLocalizedDecisionTextsToModel(
  context: ProjectContext,
  dispatch: DbotEventDispatcher,
  documentType: DocumentType,
  documentLanguage: DocumentLanguage,
) {
  const documentLanguages = mapDocumentLanguageToLocales(documentLanguage);
  return context.currentDecisions.map<DecisionTextModel>((decision) => {
    return mapDecisionTextModel(dispatch, decision, documentType, context, documentLanguages);
  });
}
export function getDecisionName(
  type: DecisionType,
  data?: DecisionData,
  lang?: Language,
): TMessage {
  if (data?.type === DecisionType.freetext) {
    const title =
      data?.localizedTitles.find((x) => x.language === lang) ?? data?.localizedTitles[0];
    return title ? { translated: title.text } : { key: `decisionType.${data.type}.action` };
  }
  return { key: `decisionType.${type}.name` };
}

export function getDecisionActionLabel(type: DecisionType, data?: DecisionData): TMessage {
  if (type === DecisionType.freetext) {
    return getDecisionName(type, data);
  }
  return { key: `decisionType.${type}.action` };
}

export function getDecisionSendToBRDescription(type: DecisionType): TMessage {
  return { key: `decisionType.${type}.br-help-text` } as TMessage;
}

export function getDecisionAttachmentsDescription(
  attachmentType: AttachmentType,
): TMessage | undefined {
  switch (attachmentType) {
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS.description",
      };
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS.description",
      };
    default:
      return undefined;
  }
}

export function getDecisionAttachmentsEmailTitle(
  attachmentType: AttachmentType,
): TMessage | undefined {
  switch (attachmentType) {
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS.email",
      };
    default:
      return undefined;
  }
}

export function getDecisionAttachmentsEmailInformation(
  attachmentType: AttachmentType,
): TMessage | undefined {
  switch (attachmentType) {
    case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
      return {
        key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS.email.information",
      };
    default:
      return undefined;
  }
}

export function getDecisionIcon(data?: DecisionData): IconName {
  if (
    data?.type === DecisionType.freetext &&
    freetextType(data) === FreetextType.INFORMATION_MATTER
  ) {
    return "chats";
  }
  return "gavel";
}

export function getMatterTypeLabel(data?: DecisionData): TMessage {
  if (
    data?.type === DecisionType.freetext &&
    freetextType(data) === FreetextType.INFORMATION_MATTER
  ) {
    return { key: "dbot.information-matter" };
  }
  return { key: "dbot.decision-matter" };
}

export function freetextType(data?: Freetext): FreetextType {
  if (data?.freetextTypeForAllDocuments === FreetextType.CUSTOMIZED) {
    return data.customizedFreetextTypePerDocument.some(
      (x) => x.customizedFreetextType === CustomizedFreetextType.DECISION_MATTER,
    )
      ? FreetextType.DECISION_MATTER
      : FreetextType.INFORMATION_MATTER;
  }
  return data?.freetextTypeForAllDocuments ?? FreetextType.INFORMATION_MATTER;
}

export function decisionToDetailsPanel(
  decision: DecisionItem,
  { documentId }: { documentId?: string } = {},
): DetailsPanel {
  return {
    type: "decision",
    id: decision.decisionId,
    documentId: documentId,
    icon: getDecisionIcon(decision.decisionData),
    heading: decision.title,
    label: getMatterTypeLabel(decision.decisionData),
    hasValidationError: decision.hasValidationError,
    tag: isDecisionTypePreview(decision.type) ? { key: "dbot.preview" } : undefined,
  };
}

export function getDecisionDetailsPanels({
  currentDecisions,
  documentId,
}: {
  documentId?: string;
  currentDecisions: DecisionItem[];
}) {
  return currentDecisions.map((d) => decisionToDetailsPanel(d, { documentId }));
}

export function getRemoveEventForPanel(
  panel: DetailsPanel | undefined,
  context: { isLocked: boolean },
): DbotEvent | DbotEvent[] | undefined {
  if (!panel || panel.isDisabled || context.isLocked) {
    return;
  }
  switch (panel.type) {
    case "decision":
      return {
        type: "dbot/remove-decision",
        decisionId: panel.id,
        onSuccess: { type: "dbot/close-details-panel" },
      };
    case "company":
      return {
        type: "dbot/change-state",
        state: removeBatchCompanyState(panel.id),
      };
    case "custom":
      if (panel.canRemove) {
        return [
          {
            type: "dbot/remove-custom-panel",
            id: panel.id,
          },
          { type: "dbot/close-details-panel" },
        ];
      }
  }
}

export function detailsPanelToRef(panel: DetailsPanel): DetailsPanelRef;
export function detailsPanelToRef(panel: DetailsPanel | undefined): DetailsPanelRef | undefined {
  switch (panel?.type) {
    case "decision":
      return {
        type: "decision",
        decisionId: panel.id,
        documentId: panel.documentId,
      };
    case "custom":
      return {
        type: "custom",
        id: panel.id,
        documentId: panel.documentId,
      };
    case "follow-up":
      return when(panel.followUp, (followUp) => ({
        type: "follow-up",
        followUpType: followUp.item.data.type,
        decisionId: followUp.decisionId,
        attachmentType: followUp.attachmentType,
      }));
    case "generalmeeting":
      return when(panel.generalMeetingStep, (step) => ({
        type: "generalmeeting",
        id: panel.id,
        step,
      }));
    case "company":
      return { type: "company", orgNo: panel.id };
  }
}

export function getOpenNextPanelEvent(
  id: string | undefined,
  panels: DetailsPanel[],
): DbotEvent | undefined {
  const nextIndex = panels.findIndex((p) => p.id === id) + 1;
  const nextPanel = detailsPanelToRef(panels[nextIndex]);
  if (nextPanel) {
    return {
      type: "dbot/open-details-panel",
      panel: nextPanel,
    };
  }
}

export function getOpenPrevPanelEvent(
  id: string | undefined,
  panels: DetailsPanel[],
): DbotEvent | undefined {
  const prevIndex = panels.findIndex((p) => p.id === id) - 1;
  const prevPanel = detailsPanelToRef(panels[prevIndex]);
  if (prevPanel) {
    return {
      type: "dbot/open-details-panel",
      panel: prevPanel,
    };
  }
}

export function getGeneralMeetingDialogEvent(panel: GeneralMeetingDetailsPanel): DbotEvent {
  switch (panel.step.type) {
    case "registration":
      return {
        type: "dbot/show-general-meeting-dialog",
      };
    case "choiceOfMeetingChair":
      return {
        type: "dbot/show-general-meeting-dialog",
        voteType: GeneralMeetingVoteType.meetChair,
      };
    case "approveAgenda":
      return {
        type: "dbot/show-general-meeting-dialog",
        voteType: GeneralMeetingVoteType.approveNoticeAndAgenda,
      };
    case "decision":
      return {
        type: "dbot/show-general-meeting-dialog",
        voteType: GeneralMeetingVoteType.decision,
        decisionId: panel.step.decisionId,
      };
  }
}

export function getOpenNextGeneralMeetingDialogEvents(
  id: string | undefined,
  panels: DetailsPanel[],
): DbotEvent[] | undefined {
  const nextIndex = panels.findIndex((p) => p.id === id) + 1;
  const nextPanel = detailsPanelToRef(panels[nextIndex]);
  if (nextPanel && nextPanel.type === "generalmeeting") {
    return [
      { type: "dbot/open-details-panel", panel: nextPanel },
      getGeneralMeetingDialogEvent(nextPanel),
    ];
  }
}

export function getOpenPrevGeneralMeetingDialogEvents(
  id: string | undefined,
  panels: DetailsPanel[],
): DbotEvent[] | undefined {
  const nextIndex = panels.findIndex((p) => p.id === id) - 1;
  const nextPanel = detailsPanelToRef(panels[nextIndex]);
  if (nextPanel && nextPanel.type === "generalmeeting") {
    return [
      { type: "dbot/open-details-panel", panel: nextPanel },
      getGeneralMeetingDialogEvent(nextPanel),
    ];
  }
}

export const isDocumentLocked = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean => documentStatuses.some((d) => d.documentId === documentId && d.isLocked);

export const anyDocumentSentForSigning = (documentStatuses: ProjectDocumentStatus[]): boolean =>
  documentStatuses.some(
    (d) =>
      d.signingStatus === DocumentSigningStatus.SENT_FOR_SIGNING ||
      d.signingStatus === DocumentSigningStatus.SIGNING_IN_PROGRESS,
  );

export const isDocumentSentForSigning = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) =>
      d.documentId === documentId &&
      (d.signingStatus === DocumentSigningStatus.SENT_FOR_SIGNING ||
        d.signingStatus === DocumentSigningStatus.SIGNING_IN_PROGRESS),
  );

export const isDocumentSentForReview = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) => d.documentId === documentId && d.signingStatus === DocumentSigningStatus.SENT_FOR_REVIEW,
  );

export const isDocumentSentForVoting = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) => d.documentId === documentId && d.signingStatus === DocumentSigningStatus.SENT_FOR_VOTING,
  );

export const isDocumentSigned = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) =>
      d.documentId === documentId && d.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE,
  );

export const isDocumentUploadedToSharepoint = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  documentStatuses.some(
    (d) =>
      d.documentId === documentId &&
      d.eventLogs.some((e) => e.eventType === EventType.SharepointSyncEvent),
  );

export const isDocumentSent = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
) => documentStatuses.some((d) => d.documentId === documentId && d.sentAt);

export const getDocumentStatus = (
  { documentStatuses }: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
) => documentStatuses.find((d) => d.documentId === documentId);

export const documentReopenLabel = (
  context: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): TMessage =>
  isDocumentSigned(context, documentId)
    ? { key: "dbot.action.reopen" }
    : { key: "dbot.action.continue-editing" };

const documentStatusesAllowingEditing = [
  DocumentSigningStatus.SENT_FOR_SIGNING,
  DocumentSigningStatus.SIGNING_IN_PROGRESS,
  DocumentSigningStatus.SIGNING_COMPLETE,
  DocumentSigningStatus.UNKNOWN,
];

export const canDocumentBeReopened = (
  context: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): boolean =>
  context.documentStatuses.some(
    (d) =>
      d.documentId === documentId &&
      d.sentAt &&
      documentStatusesAllowingEditing.includes(d.signingStatus),
  );

export function isLanguageChanged(
  documentLanguages: Language[],
  textModels: LocalizedStringFieldModel[],
): boolean {
  return !isEqual(sortBy(documentLanguages), sortBy(textModels.map((t) => t.locale)));
}

export const toCompanyDetailsPanel = ({
  name,
  orgNo,
  isMainCompany,
}: {
  name: string;
  orgNo: string;
  isMainCompany?: boolean;
}): DetailsPanel => ({
  type: "company",
  id: orgNo,
  label: { translated: formatOrgNum(orgNo) },
  heading: { translated: name },
  icon: "buildings",
  isDisabled: isMainCompany,
});

export const getMainProjectCompany = ({
  companyInfo: { company },
}: ProjectContext): ProjectCompany => ({
  orgNo: company.organizationnumber,
  name: company.name,
  isMainCompany: true,
});

export const getProjectCompanies = (ctx: ProjectContext): ProjectCompany[] => {
  return unionBy(
    [
      getMainProjectCompany(ctx),
      ...sortedCompanies(
        ctx.companies
          .filter((c) => c.orgNo in ctx.batchData)
          .map((c) => ({ orgNo: c.orgNo, name: c.name })),
      ),
    ],
    "orgNo",
  );
};

export const getCompanyDetailsPanels = (ctx: ProjectContext): DetailsPanel[] =>
  getProjectCompanies(ctx).map(toCompanyDetailsPanel);

export function isInformationMatterInDocument(
  decisionData: DecisionData | undefined,
  documentType: DocumentType,
) {
  return (
    (decisionData?.type === DecisionType.freetext &&
      decisionData.freetextTypeForAllDocuments === FreetextType.INFORMATION_MATTER) ||
    (decisionData?.type === DecisionType.freetext &&
      decisionData.freetextTypeForAllDocuments === FreetextType.CUSTOMIZED &&
      decisionData.customizedFreetextTypePerDocument.some((d) => {
        return (
          d.documentType === documentType &&
          d.customizedFreetextType === CustomizedFreetextType.INFORMATION_MATTER
        );
      }))
  );
}

export const getDecisionAttachmentsInDocuments = (
  decisionItem: DecisionItem,
  documentTypes: DocumentType[],
): { id: string; filename: string }[] =>
  decisionItem.attachments.filter((a) =>
    documentTypes.some((dt) => a.selectedDocuments.includes(dt)),
  );

export const supportsAttachments = (context: ProjectContext) => !context.isBatchProject;

export const updateArticlesModeModel = (
  value: boolean | undefined,
): RadioFieldModel<boolean | undefined> => {
  return new RadioFieldModel<boolean | undefined>({
    value,
    required: true,
    options: [true, false],
    idFn: (value) => (value === undefined ? "undefined" : String(value)),
    labelFn: (value) =>
      value === true
        ? {
            key: "decisions.increaseShareCapitalAgainstCash.updateArticlesAutomatically.automatic",
          }
        : value === false
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.updateArticlesAutomatically.manual",
            }
          : { translated: "" },
  });
};

export function findStateForDecision(inputStates: InputState[], decisionId: string) {
  return inputStates.find((i) => i.decisionId === decisionId) ?? last(inputStates);
}

export function makeCountryRadioFieldModel(locale: Language, selectedCountry?: string) {
  const countries = getAllCountriesSortedByName(locale);
  const countryCode = selectedCountry ?? norway;
  return new RadioFieldModel<{ code: string; name: string }>({
    options: countries,
    value: countries.find((c) => c.code === countryCode) ?? countries[0],
    idFn: (item) => item.code,
    labelFn: (item) => ({ translated: item.name }),
  });
}
